import React, { useEffect, useState, useMemo } from 'react';
import { 
  DataGrid, 
  GridToolbar,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarDensitySelector
} from '@mui/x-data-grid';
import {
  Fab,
  IconButton,
  MenuItem,
  Menu,
  Select,
  FormControl,
  InputLabel,
  Chip,
  OutlinedInput,
  Alert,
  Snackbar,
  Button,
  ButtonGroup,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Box,
  Paper,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  TextField
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { collection, getDocs, query, where, deleteDoc, doc, updateDoc } from 'firebase/firestore';
import { db } from '../Configuration';
import { getStudentStatusLabel, isActiveLikeStatus, STUDENT_STATUS, getFatherNameFieldLabels } from '../utils/studentStatus';
import {
  dedupeAttendanceBuckets,
  ATTENDANCE_PCT_GOOD_ABOVE,
  ATTENDANCE_PCT_WARN_MIN
} from '../utils/attendanceMath';
import { useFirebaseData } from '../contexts/FirebaseDataContext';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import AssignmentIcon from '@mui/icons-material/Assignment';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import Collapse from '@mui/material/Collapse';
import Tooltip from '@mui/material/Tooltip';
import AddStudentDialog from './AddStudents';
import ExcelImportDialog from './ExcelImportDialog';
import GradebookDialog from './GradebookDialog';
import dayjs from 'dayjs';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { isUrduText, getUrduSxProps } from '../utils/urduFont';
import { isAdmin, getGenderFilter } from '../utils/permissions';
import * as XLSX from 'xlsx-js-style';

const StudentList = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Get data from context
  const {
    students: contextStudents,
    courses: contextCourses,
    campuses: contextCampuses,
    tests: contextTests,
    attendance: contextAttendance,
    studentMarks: contextStudentMarks,
    loading: contextLoading,
    initialLoadComplete,
    fetchStudentMarks,
    refreshCollection,
    updateLocalStudent,
    getStudentsByCampus,
    getTestsByCampus,
    getTestsByCourse,
    getMarksByCourseAndCampus,
    getMarksForStudent
  } = useFirebaseData();

  // Local state for filtered/processed data
  const [students, setStudents] = useState([]);
  const [tests, setTests] = useState([]);
  const [allTests, setAllTests] = useState([]);
  const [selectedCampus, setSelectedCampus] = useState('');
  const [selectedCourse, setSelectedCourse] = useState('');
  const [loading, setLoading] = useState(true);
  const [dashboardGenderFilter, setDashboardGenderFilter] = useState('');
  const [dashboardStatusFilter, setDashboardStatusFilter] = useState('');

  // Use context data for campuses and courses
  const campuses = contextCampuses;
  const courses = contextCourses;

  // Subjects/tests for Student Management must match the selected campus (same rule as Course List)
  const coursesForCampus = useMemo(() => {
    if (!selectedCampus) return [];
    return contextCourses.filter(
      course => course.campusCodes && course.campusCodes.includes(selectedCampus)
    );
  }, [contextCourses, selectedCampus]);

  const [openAddStudentDialog, setOpenAddStudentDialog] = useState(false);
  const [openImportDialog, setOpenImportDialog] = useState(false);
  const [openGradebookDialog, setOpenGradebookDialog] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [attendanceData, setAttendanceData] = useState({});
  const [statusUpdating, setStatusUpdating] = useState({});
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [detailStudent, setDetailStudent] = useState(null);
  const [detailAttendanceSummary, setDetailAttendanceSummary] = useState([]);
  const [detailSessionHistory, setDetailSessionHistory] = useState([]);
  const [detailMarks, setDetailMarks] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [allAttendanceDialog, setAllAttendanceDialog] = useState(false);
  const [allAttendanceData, setAllAttendanceData] = useState([]);
  const [loadingAllAttendance, setLoadingAllAttendance] = useState(false);
  const [selectedAttendanceRows, setSelectedAttendanceRows] = useState([]);
  const [profileExpanded, setProfileExpanded] = useState(false);
  const [detailedMarksExpanded, setDetailedMarksExpanded] = useState(false);
  const [marksDownloadMenuAnchor, setMarksDownloadMenuAnchor] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editedStudent, setEditedStudent] = useState(null);
  const [saving, setSaving] = useState(false);
  const [inactiveReasonDialog, setInactiveReasonDialog] = useState(false);
  const [inactiveReason, setInactiveReason] = useState('');
  const [studentToMarkInactive, setStudentToMarkInactive] = useState(null);
  const [marksByStudent, setMarksByStudent] = useState({});

  const selectedCourseObj = selectedCourse
    ? courses.find(c => c.id === selectedCourse || c.id === selectedCourse.toString())
    : null;
  const selectedCourseLabel = selectedCourseObj?.name || (selectedCourse ? selectedCourse : 'All Subjects');
  const selectedTestsLabel = selectedCourse
    ? `${tests.length} test${tests.length === 1 ? '' : 's'}`
    : `${allTests.length} test${allTests.length === 1 ? '' : 's'} (all subjects)`;

  const handleOpenDialog = () => setOpenAddStudentDialog(true);
  const handleCloseDialog = async () => {
    await refreshCollection('students'); // Refresh from Firebase
    setOpenAddStudentDialog(false);
  };

  const handleOpenImportDialog = () => setOpenImportDialog(true);
  const handleCloseImportDialog = async () => {
    await refreshCollection('students'); // Refresh from Firebase
    setOpenImportDialog(false);
  };

  const handleOpenGradebookDialog = () => setOpenGradebookDialog(true);
  const handleCloseGradebookDialog = async () => {
    // Refresh marks and students so grid reflects latest saves from Gradebook
    await refreshCollection('studentMarks');
    await refreshCollection('students');
    await fetchData();
    setOpenGradebookDialog(false);
  };

  useEffect(() => {
    if (initialLoadComplete) {
      fetchStudentMarks();
    }
  }, [initialLoadComplete, fetchStudentMarks]);

  // Auto-select campus if only one is available (using context data)
  useEffect(() => {
    if (initialLoadComplete && campuses.length === 1 && !selectedCampus) {
      setSelectedCampus(campuses[0].campusCode);
    }
  }, [initialLoadComplete, campuses, selectedCampus]);

  // Apply filters when coming from Dashboard via URL params
  useEffect(() => {
    const campusCodeParam = searchParams.get('campusCode') || '';
    const genderParam = (searchParams.get('gender') || '').toLowerCase();
    const statusParam = (searchParams.get('status') || '').toLowerCase();

    if (campusCodeParam && campusCodeParam !== selectedCampus) {
      setSelectedCampus(campusCodeParam);
      setSelectedCourse(''); // reset subject when landing from dashboard
    }

    setDashboardGenderFilter(genderParam === 'male' || genderParam === 'female' ? genderParam : '');
    setDashboardStatusFilter(statusParam === 'active' || statusParam === 'inactive' ? statusParam : '');
  }, [searchParams]);

  // Drop subject selection if it does not belong to the current campus (e.g. after campus change)
  useEffect(() => {
    if (!selectedCampus || !selectedCourse) return;
    const stillValid = coursesForCampus.some(
      c => c.id === selectedCourse || c.id === selectedCourse.toString()
    );
    if (!stillValid) setSelectedCourse('');
  }, [selectedCampus, selectedCourse, coursesForCampus]);

  // Process data from context with filters
  const fetchData = async () => {
    setLoading(true);
    
    // If no campus selected, clear grid
    if (!selectedCampus) {
      setStudents([]);
      setTests([]);
      setLoading(false);
      return;
    }

    // Wait for context to load
    if (!initialLoadComplete) {
      return;
    }

    try {
      // Use context data - filter students by selected campus
      let studentList = contextStudents.filter(student =>
        student.campusCode === selectedCampus
      );

      const genderFilter = dashboardGenderFilter || getGenderFilter();
      if (genderFilter) {
        studentList = studentList.filter(s =>
          (s.gender || '').toLowerCase() === genderFilter.toLowerCase()
        );
      }

      if (dashboardStatusFilter) {
        if (dashboardStatusFilter === 'active') {
          studentList = studentList.filter(s => isActiveLikeStatus(s.status));
        } else if (dashboardStatusFilter === 'inactive') {
          studentList = studentList.filter(s => (s.status || 'Active') === 'InActive');
        }
      }

      // Use context data - filter tests by selected campus
      let testList = contextTests.filter(test =>
        test.campusCodes && test.campusCodes.includes(selectedCampus)
      );

      // Sort tests by sequence number for proper ordering
      const sortedTests = [...testList].sort((a, b) => {
        const seqA = a.sequence || 999; // Default to 999 if no sequence
        const seqB = b.sequence || 999; // Default to 999 if no sequence
        return seqA - seqB;
      });

      // Special handling for Bayan ul Quran:
      // - Do NOT require physical tests in Firestore
      // - Always expose exactly 108 tests with maxScore 5
      // - Apply for Bayan courses even when no subject is selected (for Gradebook "all tests")
      const isBayanCourse = (course) => {
        const name = (course?.name || '').toString().toLowerCase();
        return name.includes('bayan') && name.includes('quran');
      };

      const bayanTestsForCourse = (courseId) =>
        Array.from({ length: 108 }, (_, index) => {
          const num = index + 1;
          return {
            id: `bayan-${courseId}-${num}`,
            courseId,
            testName: `B.Q ${num}`,
            maxScore: 5,
            sequence: num
          };
        });

      // Build a map to dedupe tests (real + virtual)
      const testsById = new Map();
      sortedTests.forEach(t => testsById.set(t.id, t));

      // Add virtual Bayan tests for all Bayan courses (campus-filtered)
      coursesForCampus
        .filter(isBayanCourse)
        .forEach(course => {
          bayanTestsForCourse(course.id).forEach(t => testsById.set(t.id, t));
        });

      let extendedTests = Array.from(testsById.values());
      let filteredTests = [];

      if (selectedCourse) {
        const currentCourse =
          coursesForCampus.find(
            c => c.id === selectedCourse || c.id === selectedCourse.toString()
          ) || {};

        if (isBayanCourse(currentCourse)) {
          const bayanTests = bayanTestsForCourse(selectedCourse);
          filteredTests = bayanTests;
        } else {
          filteredTests = extendedTests.filter(
            test =>
              test.courseId === selectedCourse ||
              test.courseId === selectedCourse.toString()
          );
        }
      } else {
        filteredTests = [];
      }

      // Keep a copy of all tests (including virtual Bayan tests) for detail/print views and Gradebook without subject filter
      setAllTests(extendedTests);

      // Build marks map from StudentMarks (row-wise) for this campus/course
      const marksByStudent = getMarksByCourseAndCampus(selectedCampus, selectedCourse);
      setMarksByStudent(marksByStudent);

      // Map scores, total marks, percentage using StudentMarks
      // First, determine which tests are conducted (if ANY student has marks > 0 for that test)
      const conductedTestsMap = {};
      filteredTests.forEach(test => {
        // Check if ANY student has marks > 0 for this test
        const testConducted = Object.values(marksByStudent).some(s => (s?.[test.id] ?? 0) > 0);
        conductedTestsMap[test.id] = testConducted;
      });
      
      const updated = studentList.map(student => {
        let obtainedMarks = 0;
        let totalAvailableMarks = 0;
        let conductedTestsMaxMarks = 0;
        let conductedTests = 0;
        
        const scores = { ...student };
        filteredTests.forEach(test => {
          const key = test.id;
          const score = marksByStudent[student.id]?.[test.id] ?? 0;
          scores[key] = score;
          
          // Add to total available marks (for all tests)
          totalAvailableMarks += test.maxScore;
          
          // Only count tests that are conducted (if ANY student has marks > 0)
          if (conductedTestsMap[test.id]) {
            obtainedMarks += score;
            conductedTestsMaxMarks += test.maxScore;
            conductedTests += 1;
          }
        });
        
        scores.obtainedMarks = obtainedMarks;
        scores.totalAvailableMarks = totalAvailableMarks;
        scores.conductedTestsMaxMarks = conductedTestsMaxMarks;
        scores.conductedTests = conductedTests;
        scores.totalTests = filteredTests.length;
        // Only calculate percentage if there are conducted tests with marks
        scores.percentage = (conductedTestsMaxMarks > 0) 
          ? ((obtainedMarks / conductedTestsMaxMarks) * 100).toFixed(2) 
          : 'N/A';
        return scores;
      });

      // Calculate attendance percentages if a course is selected
      if (selectedCourse && updated.length > 0) {
        const studentIds = updated.map(student => student.id);
        const attendancePercentages = await calculateAttendancePercentage(studentIds, selectedCourse);
        setAttendanceData(attendancePercentages);
        
        // Add attendance data to student records
        updated.forEach(student => {
          const attendance = attendancePercentages[student.id];
          if (attendance) {
            student.attendancePercentage = attendance.percentage;
            student.attendancePresent = attendance.present;
            student.attendanceConducted = attendance.conducted;
            student.attendanceTotalPlanned = attendance.totalPlanned;
          } else {
            student.attendancePercentage = 0;
            student.attendancePresent = 0;
            student.attendanceConducted = 0;
            student.attendanceTotalPlanned = 0;
          }
        });
      } else {
        setAttendanceData({});
        // Clear attendance data when no course is selected
        updated.forEach(student => {
          student.attendancePercentage = null;
          student.attendancePresent = null;
          student.attendanceConducted = null;
          student.attendanceTotalPlanned = null;
        });
      }

      // Sort students: Male first, then Female, then by ID within each gender
      const sortedStudents = updated.sort((a, b) => {
        const genderA = a.gender || '';
        const genderB = b.gender || '';
        
        // Male comes first (return -1), Female comes second (return 1)
        if (genderA.toLowerCase() === 'male' && genderB.toLowerCase() !== 'male') {
          return -1;
        }
        if (genderA.toLowerCase() !== 'male' && genderB.toLowerCase() === 'male') {
          return 1;
        }
        
        // If both same gender, sort by ID (numeric)
        return a.id.localeCompare(b.id, undefined, { numeric: true });
      });

      setStudents(sortedStudents);
      setTests(filteredTests);
      
      if (updated.length === 0) {
        showSnackbar('No students found for the selected criteria', 'info');
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      showSnackbar('Error loading student data. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Process data when context is loaded or filters change
  useEffect(() => {
    if (initialLoadComplete) {
      fetchData();
    }
  }, [selectedCampus, selectedCourse, initialLoadComplete, contextStudents, contextTests, coursesForCampus, contextStudentMarks]);

  const handleDeleteStudent = async student => {
    if (!isAdmin() || !student) return;
    if (window.confirm(`Are you sure you want to delete student "${student.name}"? This action cannot be undone.`)) {
      try {
        const q = query(collection(db, 'Students'), where('id', '==', student.id));
        const snap = await getDocs(q);
        snap.forEach(d => deleteDoc(d.ref));
        showSnackbar(`Student "${student.name}" deleted successfully`, 'success');
        if (detailStudent?.id === student.id) {
          handleDetailDialogClose();
        }
        await refreshCollection('students'); // Refresh from Firebase
      } catch (err) {
        console.error('Error deleting student:', err);
        showSnackbar('Error deleting student. Please try again.', 'error');
      }
    }
  };

  const handleStatusUpdate = async (student, newStatus) => {
    const currentStatus = student.status || 'Active';
    if (currentStatus === newStatus) {
      return;
    }

    if (!student.docId) {
      showSnackbar('Unable to locate student record. Please refresh and try again.', 'error');
      return;
    }

    // If marking as InActive, show reason dialog
    if (newStatus === 'InActive') {
      setStudentToMarkInactive(student);
      setInactiveReason(student.reason || '');
      setInactiveReasonDialog(true);
      return;
    }

    // For marking as Active, proceed directly
    setStatusUpdating(prev => ({ ...prev, [student.id]: true }));

    try {
      const studentRef = doc(db, 'Students', student.docId);
      await updateDoc(studentRef, { status: newStatus });

      setStudents(prev => prev.map(item => (
        item.id === student.id ? { ...item, status: newStatus } : item
      )));

      showSnackbar(`Student "${student.name}" marked as ${newStatus}`, 'success');
    } catch (error) {
      console.error('Error updating student status:', error);
      showSnackbar('Error updating student status. Please try again.', 'error');
    } finally {
      setStatusUpdating(prev => {
        const updated = { ...prev };
        delete updated[student.id];
        return updated;
      });
    }
  };

  const handleConfirmInactive = async () => {
    if (!studentToMarkInactive) return;

    if (!inactiveReason.trim()) {
      showSnackbar('Please provide a reason for marking student as InActive', 'warning');
      return;
    }

    setStatusUpdating(prev => ({ ...prev, [studentToMarkInactive.id]: true }));
    setInactiveReasonDialog(false);

    try {
      const studentRef = doc(db, 'Students', studentToMarkInactive.docId);
      await updateDoc(studentRef, { 
        status: 'InActive',
        reason: inactiveReason.trim()
      });

      setStudents(prev => prev.map(item => (
        item.id === studentToMarkInactive.id 
          ? { ...item, status: 'InActive', reason: inactiveReason.trim() } 
          : item
      )));

      showSnackbar(`Student "${studentToMarkInactive.name}" marked as InActive`, 'success');
    } catch (error) {
      console.error('Error updating student status:', error);
      showSnackbar('Error updating student status. Please try again.', 'error');
    } finally {
      setStatusUpdating(prev => {
        const updated = { ...prev };
        delete updated[studentToMarkInactive.id];
        return updated;
      });
      setStudentToMarkInactive(null);
      setInactiveReason('');
    }
  };

  const handleCancelInactive = () => {
    setInactiveReasonDialog(false);
    setStudentToMarkInactive(null);
    setInactiveReason('');
  };

  const fetchStudentAttendanceDetails = async (studentId, studentCampusCode) => {
    try {
      const attendanceRef = collection(db, 'Attendance');
      
      // Try to get courses from cache first
      let courseTotalClasses = {};
      const cachedCourses = localStorage.getItem('courseTotalClasses');
      const cacheTime = localStorage.getItem('courseTotalClasses_timestamp');
      const now = Date.now();
      
      // Use cache if it's less than 1 hour old
      if (cachedCourses && cacheTime && (now - parseInt(cacheTime) < 3600000)) {
        courseTotalClasses = JSON.parse(cachedCourses);
      } else {
        // Fetch courses and cache them
        const coursesRef = collection(db, 'Courses');
        const coursesSnapshot = await getDocs(coursesRef);
        coursesSnapshot.forEach(doc => {
          const data = doc.data();
          courseTotalClasses[data.name] = data.totalClasses || 0;
        });
        localStorage.setItem('courseTotalClasses', JSON.stringify(courseTotalClasses));
        localStorage.setItem('courseTotalClasses_timestamp', now.toString());
      }
      
      // Fetch attendance records FILTERED BY CAMPUS to count conducted classes
      let allAttendanceSnapshot;
      if (studentCampusCode) {
        const campusQuery = query(attendanceRef, where('campusCode', '==', studentCampusCode));
        allAttendanceSnapshot = await getDocs(campusQuery);
      } else {
        // Fallback to all attendance if no campus provided
        allAttendanceSnapshot = await getDocs(attendanceRef);
      }
      
      // Count conducted classes by course (filtered by campus)
      const conductedClassesBySubject = {};
      allAttendanceSnapshot.forEach(doc => {
        const courseName = doc.data().courseName;
        conductedClassesBySubject[courseName] = (conductedClassesBySubject[courseName] || 0) + 1;
      });
      
      // Fetch student's attendance records
      const [presentSnapshot, absentSnapshot, leaveSnapshot] = await Promise.all([
        getDocs(query(attendanceRef, where('presentStudents', 'array-contains', studentId))),
        getDocs(query(attendanceRef, where('absentStudents', 'array-contains', studentId))),
        getDocs(query(attendanceRef, where('leaveStudents', 'array-contains', studentId)))
      ]);

      const summaryMap = {};
      const sessions = [];

      const ensureSummary = (courseName) => {
        if (!summaryMap[courseName]) {
          summaryMap[courseName] = {
            courseName,
            present: 0,
            absent: 0,
            leaves: 0,
            conducted: conductedClassesBySubject[courseName] || 0,
            totalPlanned: courseTotalClasses[courseName] || 0
          };
        }
        return summaryMap[courseName];
      };

      const docMap = new Map();
      const mergeAttendanceDocs = (snap) => {
        snap.forEach((docSnap) => {
          if (!docMap.has(docSnap.id)) docMap.set(docSnap.id, docSnap);
        });
      };
      mergeAttendanceDocs(presentSnapshot);
      mergeAttendanceDocs(absentSnapshot);
      mergeAttendanceDocs(leaveSnapshot);

      docMap.forEach((docSnap) => {
        const data = docSnap.data();
        const courseName = data.courseName || 'Unknown Course';
        const b = dedupeAttendanceBuckets(data);
        let statusKey;
        let statusLabel;
        if (b.presentSet.has(studentId)) {
          statusKey = 'present';
          statusLabel = 'Present';
        } else if (b.leaveSet.has(studentId)) {
          statusKey = 'leaves';
          statusLabel = 'Leave';
        } else if (b.absentSet.has(studentId)) {
          statusKey = 'absent';
          statusLabel = 'Absent';
        } else {
          return;
        }
        const summary = ensureSummary(courseName);
        summary[statusKey] += 1;

        const start = data.startTime ? dayjs(data.startTime.replace(/-(\d{1,2}):(\d{2})$/, ' $1:$2'), 'YYYY-MM-DD HH:mm') : null;
        const end = data.endTime ? dayjs(data.endTime.replace(/-(\d{1,2}):(\d{2})$/, ' $1:$2'), 'YYYY-MM-DD HH:mm') : null;

        sessions.push({
          id: docSnap.id,
          courseName,
          status: statusLabel,
          startDisplay: start && start.isValid() ? start.format('DD/MM/YYYY - hh:mm A') : (data.startTime || 'N/A'),
          endDisplay: end && end.isValid() ? end.format('DD/MM/YYYY - hh:mm A') : (data.endTime || 'N/A'),
          startValue: start && start.isValid() ? start.valueOf() : 0,
          topic: data.topic || 'N/A',
          comments: data.comments || 'N/A',
          reason: statusKey === 'leaves' ? (data.leaveReasons?.[studentId] || 'N/A') : 'N/A'
        });
      });

      const summary = Object.values(summaryMap).map(item => ({
        ...item,
        percentage: item.conducted > 0
          ? (((item.present + item.leaves) / item.conducted) * 100).toFixed(1)
          : '0.0'
      })).sort((a, b) => a.courseName.localeCompare(b.courseName));

      const sortedSessions = sessions
        .sort((a, b) => b.startValue - a.startValue)
        .slice(0, 8);

      return {
        summary,
        sessions: sortedSessions
      };
    } catch (error) {
      console.error('Error fetching student attendance details:', error);
      showSnackbar('Error loading attendance details. Please try again.', 'error');
      return {
        summary: [],
        sessions: []
      };
    }
  };

  const handleViewAllAttendance = async () => {
    if (!detailStudent) return;
    
    setLoadingAllAttendance(true);
    setAllAttendanceDialog(true);
    
    try {
      const attendanceRef = collection(db, 'Attendance');
      const qPresent = query(attendanceRef, where('presentStudents', 'array-contains', detailStudent.id));
      const qAbsent = query(attendanceRef, where('absentStudents', 'array-contains', detailStudent.id));
      const qLeave = query(attendanceRef, where('leaveStudents', 'array-contains', detailStudent.id));

      const [presentSnapshot, absentSnapshot, leaveSnapshot] = await Promise.all([
        getDocs(qPresent), 
        getDocs(qAbsent), 
        getDocs(qLeave)
      ]);

      const allSessions = [];
      let idCounter = 1;
      const docMapAll = new Map();
      const mergeAll = (snap) => {
        snap.forEach((d) => {
          if (!docMapAll.has(d.id)) docMapAll.set(d.id, d);
        });
      };
      mergeAll(presentSnapshot);
      mergeAll(absentSnapshot);
      mergeAll(leaveSnapshot);

      docMapAll.forEach((doc) => {
        const data = doc.data();
        const b = dedupeAttendanceBuckets(data);
        let status;
        let reason = null;
        if (b.presentSet.has(detailStudent.id)) {
          status = 'Present';
        } else if (b.leaveSet.has(detailStudent.id)) {
          status = 'Leave';
          reason = data.leaveReasons && data.leaveReasons[detailStudent.id]
            ? data.leaveReasons[detailStudent.id]
            : null;
        } else if (b.absentSet.has(detailStudent.id)) {
          status = 'Absent';
        } else {
          return;
        }

        const start = dayjs(data.startTime.replace(/-(\d{1,2}):(\d{2})$/, ' $1:$2'), 'YYYY-MM-DD HH:mm');
        const end = dayjs(data.endTime.replace(/-(\d{1,2}):(\d{2})$/, ' $1:$2'), 'YYYY-MM-DD HH:mm');

        allSessions.push({
          id: idCounter++,
          courseName: data.courseName,
          startTime: start.isValid() ? start.format('DD/MM/YYYY - hh:mm A') : data.startTime,
          endTime: end.isValid() ? end.format('hh:mm A') : data.endTime,
          startValue: start.isValid() ? start.valueOf() : 0,
          date: start.isValid() ? start.format('DD/MM/YYYY') : null,
          topic: data.topic || '-',
          comments: data.comments || '-',
          status,
          reason
        });
      });

      // Sort by date (newest first)
      allSessions.sort((a, b) => b.startValue - a.startValue);
      setAllAttendanceData(allSessions);
    } catch (error) {
      console.error('Error fetching all attendance: ', error);
      setAllAttendanceData([]);
    } finally {
      setLoadingAllAttendance(false);
    }
  };

  const handlePrintAllAttendance = () => {
    const rowsToPrint = selectedAttendanceRows.length > 0
      ? allAttendanceData.filter(row => selectedAttendanceRows.includes(row.id))
      : allAttendanceData;

    if (rowsToPrint.length === 0) {
      showSnackbar('No records to print', 'warning');
      return;
    }

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8" />
        <title>Complete Attendance History - ${detailStudent?.name || 'Student'}</title>
        <link rel="preconnect" href="https://fonts.googleapis.com">
        <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
        <link href="https://fonts.googleapis.com/css2?family=Noto+Nastaliq+Urdu:wght@400;500;600;700&display=swap" rel="stylesheet">
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          h1 { color: #2E7D32; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; }
          th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
          th { background-color: #4CAF50; color: white; }
          tr:nth-child(even) { background-color: #f2f2f2; }
          .urdu {
            font-family: 'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', serif;
            font-weight: 400;
            -webkit-font-smoothing: antialiased;
            -moz-osx-font-smoothing: grayscale;
            direction: ltr;
            text-align: left;
          }
        </style>
      </head>
      <body>
        <h1>Complete Attendance History</h1>
        <p><strong>Student ID:</strong> ${detailStudent?.id || 'N/A'}</p>
        <p><strong>Student Name:</strong> ${detailStudent?.name || 'N/A'}</p>
        <table>
          <thead>
            <tr>
              <th>Course</th>
              <th>Date & Time</th>
              <th>Topic</th>
              <th>Status</th>
              <th>Leave Reason</th>
              <th>Comments</th>
            </tr>
          </thead>
          <tbody>
            ${rowsToPrint.map(row => `
              <tr>
                <td>${row.courseName}</td>
                <td>${row.startTime}</td>
                <td class="${isUrduText(String(row.topic || '')) ? 'urdu' : ''}">${row.topic}</td>
                <td>${row.status}</td>
                <td>${row.reason || '-'}</td>
                <td class="${isUrduText(String(row.comments || '')) ? 'urdu' : ''}">${row.comments || '-'}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      </body>
      </html>
    `;

    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  const buildStudentMarksBreakdown = (student) => {
    if (!student) return [];

    const courseNameMap = courses.reduce((acc, course) => {
      acc[course.id] = course.name;
      return acc;
    }, {});

    // EXACT SAME LOGIC AS GRID
    // First, determine which tests are conducted (if ANY student has marks > 0 for that test)
    const conductedTestsMap = {};
    
    // Get marksByStudent - if no course selected, get all marks
    const allMarksByStudent = selectedCourse 
      ? marksByStudent 
      : getMarksByCourseAndCampus(selectedCampus, null);
    
    // Determine conducted tests - use tests array if course selected, otherwise use allTests
    const testsToCheck = selectedCourse && tests.length > 0 ? tests : allTests;
    
    if (testsToCheck.length > 0) {
      testsToCheck.forEach(test => {
        // Check if ANY student has marks > 0 for this test (EXACT same as grid line 258)
        const testConducted = Object.values(allMarksByStudent).some(s => (s?.[test.id] ?? 0) > 0);
        conductedTestsMap[test.id] = testConducted;
      });
    }

    // Build breakdown using EXACT same logic as grid (lines 269-282)
    const breakdown = [];
    
    if (selectedCourse && tests.length > 0) {
      // Course selected - use same logic as grid
      tests.forEach(test => {
        // Only include tests that are conducted (EXACT same condition as grid line 278)
        if (conductedTestsMap[test.id]) {
          // Get score EXACT same way as grid line 271
          const score = marksByStudent[student.id]?.[test.id] ?? 0;
          const maxScore = Number(test.maxScore ?? 0) || null;
          const percentage = maxScore ? ((score / maxScore) * 100).toFixed(1) : null;

          breakdown.push({
            id: test.id,
            courseName: courseNameMap[test.courseId] || test.courseName || 'Unknown Course',
            testName: test.testName || test.id,
            score: score,
            maxScore: maxScore ?? 'N/A',
            percentage
          });
        }
      });
    } else {
      // No course selected - show all conducted tests from allTests
      allTests.forEach(test => {
        // Only include tests that are conducted
        if (conductedTestsMap[test.id]) {
          // Get score from allMarksByStudent
          const score = allMarksByStudent[student.id]?.[test.id] ?? 0;
          const maxScore = Number(test.maxScore ?? 0) || null;
          const percentage = maxScore ? ((score / maxScore) * 100).toFixed(1) : null;

          breakdown.push({
            id: test.id,
            courseName: courseNameMap[test.courseId] || test.courseName || 'Unknown Course',
            testName: test.testName || test.id,
            score: score,
            maxScore: maxScore ?? 'N/A',
            percentage
          });
        }
      });
    }

    return breakdown.sort((a, b) => {
      const courseCompare = a.courseName.localeCompare(b.courseName);
      if (courseCompare !== 0) return courseCompare;
      return a.testName.localeCompare(b.testName, undefined, { numeric: true, sensitivity: 'base' });
    });
  };

  const handleStudentDetailOpen = async (student) => {
    setDetailStudent(student);
    setDetailDialogOpen(true);
    setDetailLoading(true);

    try {
      const marksBreakdown = buildStudentMarksBreakdown(student);
      const attendanceDetails = await fetchStudentAttendanceDetails(student.id, student.campusCode);

      setDetailMarks(marksBreakdown);
      setDetailAttendanceSummary(attendanceDetails.summary);
      setDetailSessionHistory(attendanceDetails.sessions);
    } finally {
      setDetailLoading(false);
    }
  };

  const navigateToNextStudent = async () => {
    if (!detailStudent) return;
    
    const currentIndex = students.findIndex(s => s.id === detailStudent.id);
    if (currentIndex < students.length - 1) {
      const nextStudent = students[currentIndex + 1];
      await handleStudentDetailOpen(nextStudent);
    }
  };

  const navigateToPreviousStudent = async () => {
    if (!detailStudent) return;
    
    const currentIndex = students.findIndex(s => s.id === detailStudent.id);
    if (currentIndex > 0) {
      const prevStudent = students[currentIndex - 1];
      await handleStudentDetailOpen(prevStudent);
    }
  };

  const handleDetailDialogClose = () => {
    setDetailDialogOpen(false);
    setDetailStudent(null);
    setDetailAttendanceSummary([]);
    setDetailSessionHistory([]);
    setDetailMarks([]);
    setProfileExpanded(false);
    setEditMode(false);
    setEditedStudent(null);
    // Clear URL parameter when dialog closes
    if (searchParams.has('studentId')) {
      searchParams.delete('studentId');
      setSearchParams(searchParams);
    }
  };

  // Handle opening student dialog from URL parameter
  useEffect(() => {
    const studentId = searchParams.get('studentId');
    if (studentId && students.length > 0 && !detailDialogOpen) {
      const student = students.find(s => s.id === studentId);
      if (student) {
        handleStudentDetailOpen(student);
      }
    }
  }, [searchParams, students, detailDialogOpen]);

  const handlePrintStudentReport = () => {
    if (!detailStudent) return;

    // Get campus name from campuses array
    const studentCampus = campuses.find(c => c.campusCode === detailStudent.campusCode);
    const campusName = studentCampus ? studentCampus.campusName : detailStudent.campusCode || '-';

    const printWindow = window.open('', '_blank');
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Student Report - ${detailStudent.name}</title>
        <style>
          @media print {
            body { margin: 0; }
            @page { margin: 1cm; }
          }
          body {
            font-family: Arial, sans-serif;
            padding: 20px;
            direction: ltr;
          }
          .header {
            text-align: center;
            margin-bottom: 30px;
            border-bottom: 3px solid #4CAF50;
            padding-bottom: 15px;
          }
          .header h1 {
            color: #4CAF50;
            margin: 10px 0;
          }
          .section {
            margin: 30px 0;
            page-break-inside: avoid;
          }
          .section-title {
            background-color: #4CAF50;
            color: white;
            padding: 10px;
            font-size: 18px;
            font-weight: bold;
            margin-bottom: 15px;
          }
          .profile-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 15px;
            margin-bottom: 20px;
          }
          .profile-item {
            padding: 10px;
            border: 1px solid #ddd;
            border-radius: 5px;
          }
          .profile-label {
            font-weight: bold;
            color: #555;
            font-size: 12px;
            margin-bottom: 5px;
          }
          .profile-value {
            font-size: 14px;
            color: #000;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 10px;
          }
          th, td {
            border: 1px solid #ddd;
            padding: 10px;
            text-align: left;
          }
          th {
            background-color: #4CAF50;
            color: white;
            font-weight: bold;
          }
          tr:nth-child(even) {
            background-color: #f9f9f9;
          }
          .status-chip {
            padding: 4px 8px;
            border-radius: 4px;
            font-size: 12px;
            font-weight: bold;
          }
          .status-present { background-color: #4CAF50; color: white; }
          .status-absent { background-color: #f44336; color: white; }
          .status-leave { background-color: #ff9800; color: white; }
          .footer {
            margin-top: 40px;
            text-align: center;
            font-size: 12px;
            color: #666;
            border-top: 2px solid #4CAF50;
            padding-top: 15px;
          }
          .urdu {
            direction: ltr;
            text-align: left;
            font-family: 'Jameel Noori Nastaleeq', 'Noto Nastalikh Urdu', serif;
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>Student Report</h1>
          <p>Anjuman Khuddam ul Quran</p>
          <p style="font-size: 14px; color: #666;">Generated on ${new Date().toLocaleString('en-GB')}</p>
        </div>

            <!-- Profile Section -->
            <div class="section">
              <div class="section-title">📋 Student Profile</div>
              <div class="profile-grid">
                <div class="profile-item">
                  <div class="profile-label">Name</div>
                  <div class="profile-value">${detailStudent.name}</div>
                </div>
                <div class="profile-item">
                  <div class="profile-label">Student ID</div>
                  <div class="profile-value">${detailStudent.id}</div>
                </div>
                <div class="profile-item">
                  <div class="profile-label">Status</div>
                  <div class="profile-value">${detailStudent.status || 'Active'}</div>
                </div>
                <div class="profile-item">
                  <div class="profile-label">Campus Name</div>
                  <div class="profile-value">${campusName}</div>
                </div>
              </div>
            </div>

            <!-- Attendance Summary Section -->
            ${detailAttendanceSummary.length > 0 ? `
            <div class="section">
              <div class="section-title">📊 Attendance Summary</div>
              <table>
                <thead>
                  <tr>
                    <th>Course</th>
                    <th>Present</th>
                    <th>Absent</th>
                    <th>Leaves</th>
                    <th>Percentage</th>
                  </tr>
                </thead>
                <tbody>
                  ${detailAttendanceSummary.map(row => `
                    <tr>
                      <td>${row.courseName}</td>
                      <td>${row.present}</td>
                      <td>${row.absent}</td>
                      <td>${row.leaves}</td>
                      <td>${row.percentage}%</td>
                    </tr>
                  `).join('')}
                </tbody>
              </table>
            </div>
            ` : ''}

        <!-- Marks Summary Section -->
        ${detailMarks.length > 0 ? (() => {
          // Group marks by course/subject and calculate totals (same logic as UI)
          const marksByCourse = detailMarks.reduce((acc, mark) => {
            const courseName = mark.courseName;
            if (!acc[courseName]) {
              acc[courseName] = {
                marks: [],
                totalScore: 0,
                totalOutOf: 0
              };
            }
            acc[courseName].marks.push(mark);
            acc[courseName].totalScore += Number(mark.score || 0);
            const maxScore = mark.maxScore === 'N/A' ? 0 : Number(mark.maxScore || 0);
            acc[courseName].totalOutOf += maxScore;
            return acc;
          }, {});

          // Create summary array sorted A to Z
          const summary = Object.entries(marksByCourse)
            .map(([courseName, data]) => {
              const percentage = data.totalOutOf > 0 
                ? ((data.totalScore / data.totalOutOf) * 100).toFixed(2)
                : '0.00';
              return {
                courseName,
                totalScore: data.totalScore,
                totalOutOf: data.totalOutOf,
                testsConducted: data.marks.length,
                percentage
              };
            })
            .sort((a, b) => a.courseName.localeCompare(b.courseName));

          return `
          <div class="section">
            <div class="section-title">📝 Subject-Wise Marks Summary</div>
            <table>
              <thead>
                <tr>
                  <th>Subject</th>
                  <th>Total Score</th>
                  <th>Out Of</th>
                  <th>Tests Conducted</th>
                  <th>Percentage</th>
                </tr>
              </thead>
              <tbody>
                ${summary.map(row => `
                  <tr>
                    <td>${row.courseName}</td>
                    <td>${row.totalScore}</td>
                    <td>${row.totalOutOf}</td>
                    <td>${row.testsConducted}</td>
                    <td>${row.percentage}%</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
          </div>
          `;
        })() : ''}

        <div class="footer">
          <p>© Anjuman Khuddam ul Quran</p>
          <p>This is a computer-generated report</p>
        </div>
      </body>
      </html>
    `);
    
    printWindow.document.close();
    printWindow.focus();
    
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  // Download Attendance Summary as CSV
  const handleDownloadAttendanceSummary = () => {
    if (!detailStudent || detailAttendanceSummary.length === 0) {
      showSnackbar('No attendance data to download', 'warning');
      return;
    }

    const headers = ['Course', 'Present', 'Absent', 'Leaves', 'Conducted', 'Total Planned', 'Percentage'];
    const rows = detailAttendanceSummary.map(row => [
      row.courseName,
      row.present,
      row.absent,
      row.leaves,
      row.conducted,
      row.totalPlanned,
      `${row.percentage}%`
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell)}"`).join(','))
    ].join('\n');

    // Add UTF-8 BOM for proper Urdu/Arabic text display in Excel
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${detailStudent.name}_Attendance_Summary_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    showSnackbar('Attendance summary downloaded successfully', 'success');
  };

  const rawStudentsForExport = useMemo(() => {
    if (!selectedCampus) return [];
    let list = (contextStudents || []).filter(s => s?.campusCode === selectedCampus);
    const genderFilter = dashboardGenderFilter || getGenderFilter();
    if (genderFilter) {
      list = list.filter(s => (s?.gender || '').toLowerCase() === genderFilter.toLowerCase());
    }

    if (dashboardStatusFilter) {
      if (dashboardStatusFilter === 'active') {
        list = list.filter(s => isActiveLikeStatus(s?.status));
      } else if (dashboardStatusFilter === 'inactive') {
        list = list.filter(s => (s?.status || 'Active') === 'InActive');
      }
    }
    return list;
  }, [contextStudents, selectedCampus, dashboardGenderFilter, dashboardStatusFilter]);

  const excludedKeysForStudentDetailExport = useMemo(() => {
    const set = new Set([
      'Action',
      'actions',
      'testScores',
      'obtainedMarks',
      'totalAvailableMarks',
      'conductedTestsMaxMarks',
      'conductedTests',
      'totalTests',
      'percentage',
      'attendancePercentage',
      'attendancePresent',
      'attendanceConducted',
      'attendanceTotalPlanned'
    ]);

    // Exclude any test ID fields that may have been persisted on student docs
    (allTests || []).forEach(t => {
      if (t?.id !== undefined && t?.id !== null) set.add(String(t.id));
    });

    return set;
  }, [allTests]);

  const toExportableCell = (value) => {
    if (value === null || value === undefined) return '';
    if (value instanceof Date) return value.toISOString();
    if (typeof value === 'object') {
      // Avoid "[object Object]" in exports
      try {
        return JSON.stringify(value);
      } catch {
        return String(value);
      }
    }
    return String(value);
  };

  const studentExportHeaderLabel = (key) => {
    const map = {
      id: 'ID',
      nameInUrdu: 'Name (Urdu)',
      fatherName: 'Father/Husband Name',
      fatherNameInUrdu: 'Father/Husband Name (Urdu)'
    };
    if (map[key]) return map[key];

    const humanize = (k) => {
      const s = String(k || '')
        // normalize separators
        .replace(/[_\-]+/g, ' ')
        // split camelCase / PascalCase boundaries
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        // split letter<->digit boundaries
        .replace(/([A-Za-z])([0-9])/g, '$1 $2')
        .replace(/([0-9])([A-Za-z])/g, '$1 $2')
        .trim();

      if (!s) return '';

      const acronyms = new Set(['id', 'cnic', 'dob', 'riqc', 'gps']);
      return s
        .split(/\s+/)
        .map((w) => {
          const lower = w.toLowerCase();
          if (acronyms.has(lower)) return lower.toUpperCase();
          return lower.charAt(0).toUpperCase() + lower.slice(1);
        })
        .join(' ');
    };

    return humanize(key);
  };

  const buildAllStudentExportSchema = (rows) => {
    const preferredOrder = [
      'id',
      'name',
      'nameInUrdu',
      'fatherName',
      'fatherNameInUrdu',
      'status',
      'campusCode',
      'gender',
      'qualification',
      'scholarship',
      'interview',
      'dateOfBirth',
      'age',
      'phone',
      'address',
      'comments'
    ];

    const keySet = new Set();
    rows.forEach((r) => {
      if (!r || typeof r !== 'object') return;
      Object.keys(r).forEach((k) => {
        // Only student details: exclude computed/test fields
        if (excludedKeysForStudentDetailExport.has(k)) return;
        if (/^bayan-/.test(k)) return;
        keySet.add(k);
      });
    });

    const keys = Array.from(keySet);
    const preferred = preferredOrder.filter((k) => keySet.has(k));
    const remaining = keys
      .filter((k) => !preferred.includes(k))
      .sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));

    return [...preferred, ...remaining];
  };

  const handleExportAllStudentsExcel = () => {
    if (!rawStudentsForExport || rawStudentsForExport.length === 0) {
      showSnackbar('No students to export.', 'warning');
      return;
    }

    try {
      // Match grid sorting: Male first, then Female, then by ID (numeric)
      const exportRows = [...rawStudentsForExport].sort((a, b) => {
        const genderA = (a?.gender || '').toString().toLowerCase();
        const genderB = (b?.gender || '').toString().toLowerCase();

        const isMaleA = genderA === 'male';
        const isMaleB = genderB === 'male';
        if (isMaleA !== isMaleB) return isMaleA ? -1 : 1;

        return String(a?.id || '').localeCompare(String(b?.id || ''), undefined, { numeric: true, sensitivity: 'base' });
      });

      const keys = buildAllStudentExportSchema(rawStudentsForExport);
      const headers = keys.map((k) => studentExportHeaderLabel(k));

      const aoa = [
        headers,
        ...exportRows.map((s) => keys.map((k) => toExportableCell(s?.[k])))
      ];

      const ws = XLSX.utils.aoa_to_sheet(aoa);
      const range = XLSX.utils.decode_range(ws['!ref']);

      const headerStyle = {
        font: { bold: true, color: { rgb: 'FFFFFF' }, sz: 11 },
        fill: { fgColor: { rgb: '2E7D32' } }, // Green header
        alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
        border: {
          top: { style: 'thin', color: { rgb: '000000' } },
          bottom: { style: 'thin', color: { rgb: '000000' } },
          left: { style: 'thin', color: { rgb: '000000' } },
          right: { style: 'thin', color: { rgb: '000000' } }
        }
      };

      const defaultCellStyle = {
        font: { sz: 10 },
        alignment: { horizontal: 'left', vertical: 'center', wrapText: true },
        border: {
          top: { style: 'thin', color: { rgb: 'DDDDDD' } },
          bottom: { style: 'thin', color: { rgb: 'DDDDDD' } },
          left: { style: 'thin', color: { rgb: 'DDDDDD' } },
          right: { style: 'thin', color: { rgb: 'DDDDDD' } }
        }
      };

      const urduCellStyle = {
        font: { sz: 11, name: 'Noto Nastaliq Urdu' },
        alignment: {
          horizontal: 'right',
          vertical: 'center',
          wrapText: true,
          readingOrder: 2
        },
        border: defaultCellStyle.border
      };

      // Style header row
      for (let c = range.s.c; c <= range.e.c; c++) {
        const addr = XLSX.utils.encode_cell({ r: 0, c });
        if (!ws[addr]) ws[addr] = { v: '' };
        ws[addr].s = headerStyle;
      }

      // Style data cells with Urdu-aware formatting
      for (let r = 1; r <= range.e.r; r++) {
        for (let c = range.s.c; c <= range.e.c; c++) {
          const addr = XLSX.utils.encode_cell({ r, c });
          if (!ws[addr]) continue;
          const val = ws[addr].v;
          const text = typeof val === 'string' ? val : (val === null || val === undefined ? '' : String(val));
          ws[addr].s = isUrduText(text) ? urduCellStyle : defaultCellStyle;
        }
      }

      // Column widths (simple heuristic)
      ws['!cols'] = headers.map((h, idx) => {
        let maxLen = String(h || '').length;
        for (let r = 1; r <= Math.min(range.e.r, 200); r++) {
          const addr = XLSX.utils.encode_cell({ r, c: idx });
          const cell = ws[addr];
          if (!cell) continue;
          const v = cell.v === null || cell.v === undefined ? '' : String(cell.v);
          if (v.length > maxLen) maxLen = v.length;
        }
        return { wch: Math.min(60, Math.max(12, maxLen + 2)) };
      });

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Students');

      const dateStr = new Date().toISOString().split('T')[0];
      const campusPart = selectedCampus ? String(selectedCampus).replace(/[^a-zA-Z0-9]/g, '_') : 'AllCampuses';
      const filename = `Students_AllColumns_${campusPart}_${dateStr}.xlsx`;
      XLSX.writeFile(wb, filename);

      showSnackbar(`Exported successfully: ${filename}`, 'success');
    } catch (e) {
      console.error('Export all students excel failed:', e);
      showSnackbar('Export failed. Please try again.', 'error');
    }
  };

  const StudentsGridToolbar = () => (
    <GridToolbarContainer sx={{ justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
        <GridToolbarColumnsButton />
        <GridToolbarFilterButton />
        <GridToolbarDensitySelector />
        <Button
          variant="outlined"
          size="small"
          startIcon={<DownloadIcon />}
          onClick={handleExportAllStudentsExcel}
          sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
        >
          Export All Columns
        </Button>
      </Box>
    </GridToolbarContainer>
  );

  // Download Marks Summary as CSV
  const handleDownloadMarksSummary = () => {
    if (!detailStudent || detailMarks.length === 0) {
      showSnackbar('No marks data to download', 'warning');
      return;
    }

    // Group marks by course/subject and calculate totals (same logic as UI)
    const marksByCourse = detailMarks.reduce((acc, mark) => {
      const courseName = mark.courseName;
      if (!acc[courseName]) {
        acc[courseName] = {
          marks: [],
          totalScore: 0,
          totalOutOf: 0
        };
      }
      acc[courseName].marks.push(mark);
      acc[courseName].totalScore += Number(mark.score || 0);
      const maxScore = mark.maxScore === 'N/A' ? 0 : Number(mark.maxScore || 0);
      acc[courseName].totalOutOf += maxScore;
      return acc;
    }, {});

    const summary = Object.entries(marksByCourse)
      .map(([courseName, data]) => {
        const percentage = data.totalOutOf > 0 
          ? ((data.totalScore / data.totalOutOf) * 100).toFixed(2)
          : '0.00';
        return {
          courseName,
          totalScore: data.totalScore,
          totalOutOf: data.totalOutOf,
          testsConducted: data.marks.length,
          percentage
        };
      })
      .sort((a, b) => a.courseName.localeCompare(b.courseName));

    const headers = ['Subject', 'Total Score', 'Out Of', 'Tests Conducted', 'Percentage'];
    const rows = summary.map(row => [
      row.courseName,
      row.totalScore,
      row.totalOutOf,
      row.testsConducted,
      `${row.percentage}%`
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell)}"`).join(','))
    ].join('\n');

    // Add UTF-8 BOM for proper Urdu/Arabic text display in Excel
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${detailStudent.name}_Marks_Summary_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    showSnackbar('Marks summary downloaded successfully', 'success');
  };

  // Download Marks Breakdown as CSV
  const handleDownloadMarksBreakdown = () => {
    if (!detailStudent || detailMarks.length === 0) {
      showSnackbar('No marks data to download', 'warning');
      return;
    }

    const headers = ['Subject', 'Assessment', 'Score', 'Out Of', 'Percentage'];
    const rows = detailMarks.map(mark => [
      mark.courseName,
      mark.testName,
      mark.score,
      mark.maxScore === 'N/A' ? '' : mark.maxScore,
      mark.percentage ? `${mark.percentage}%` : 'N/A'
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell)}"`).join(','))
    ].join('\n');

    // Add UTF-8 BOM for proper Urdu/Arabic text display in Excel
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${detailStudent.name}_Marks_Breakdown_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    showSnackbar('Marks breakdown downloaded successfully', 'success');
  };

  const handleEditClick = () => {
    console.log('Edit clicked, detailStudent:', detailStudent);
    // Make sure all fields are properly copied
    const studentCopy = {
      ...detailStudent,
      // Ensure empty strings for null/undefined values so Select doesn't become uncontrolled
      qualification: detailStudent.qualification || '',
      interview: detailStudent.interview || '',
      scholarship: detailStudent.scholarship || '',
      status: detailStudent.status || 'Active',
      gender: detailStudent.gender || '',
      femaleNameRelation: detailStudent.femaleNameRelation || '',
      dateOfBirth: detailStudent.dateOfBirth || '',
      age: detailStudent.age ?? ''
    };
    console.log('Setting editedStudent to:', studentCopy);
    setEditMode(true);
    setEditedStudent(studentCopy);
  };

  const handleCancelEdit = () => {
    setEditMode(false);
    setEditedStudent(null);
  };

  const handleFieldChange = (field, value) => {
    setEditedStudent(prev => {
      const next = { ...prev, [field]: value };
      if (field === 'gender' && value !== 'Female') {
        next.femaleNameRelation = '';
      }
      return next;
    });
  };

  const calculateAgeFromDob = (dob) => {
    if (!dob) return '';
    const parsed = new Date(dob);
    if (Number.isNaN(parsed.getTime())) return '';

    const today = new Date();
    let years = today.getFullYear() - parsed.getFullYear();
    const monthDiff = today.getMonth() - parsed.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < parsed.getDate())) {
      years -= 1;
    }
    return years < 0 ? '' : String(years);
  };

  const handleDobChange = (dob) => {
    setEditedStudent(prev => {
      if (!prev) return prev;
      const next = { ...prev, dateOfBirth: dob };
      // Auto-calculate age only when DOB is selected; otherwise keep age as-is
      if (dob) next.age = calculateAgeFromDob(dob);
      return next;
    });
  };

  const handleSaveEdit = async () => {
    console.log('Save button clicked');
    console.log('editedStudent:', editedStudent);
    
    if (!editedStudent) {
      showSnackbar('No student data to save.', 'error');
      console.error('Save failed - editedStudent is null');
      return;
    }

    if (!editedStudent.docId) {
      showSnackbar('Unable to locate student record. Missing document ID.', 'error');
      console.error('Save failed - missing docId:', editedStudent);
      return;
    }

    setSaving(true);
    try {
      const studentRef = doc(db, 'Students', editedStudent.docId);
      
      // Remove fields that shouldn't be updated (computed fields)
      const { docId, totalMarks, maxTotal, obtainedMarks, totalAvailableMarks, conductedTests, totalTests,
              percentage, attendancePercentage, attendancePresent, 
              attendanceConducted, attendanceTotalPlanned, ...dataToUpdate } = editedStudent;
      
      // Also remove testScores as it's managed separately
      delete dataToUpdate.testScores;

      if ((dataToUpdate.gender || '') !== 'Female') {
        dataToUpdate.femaleNameRelation = '';
      }
      
      console.log('Saving to Firestore with docId:', editedStudent.docId);
      console.log('Data to update:', dataToUpdate);
      
      await updateDoc(studentRef, dataToUpdate);

      console.log('Update successful');

      // Update local state
      setStudents(prev => prev.map(item => 
        item.id === editedStudent.id ? { ...item, ...dataToUpdate } : item
      ));

      // Update the detail view
      setDetailStudent({ ...detailStudent, ...dataToUpdate });
      setEditMode(false);
      setEditedStudent(null);
      
      showSnackbar(`Student "${editedStudent.name}" updated successfully`, 'success');
    } catch (error) {
      console.error('Error updating student:', error);
      console.error('Error details:', error.message);
      showSnackbar(`Error: ${error.message}`, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleCampusChange = e => setSelectedCampus(e.target.value);
  const handleCourseChange = e => setSelectedCourse(e.target.value);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Calculate attendance percentage for students in a specific course
  const calculateAttendancePercentage = async (studentIds, courseId) => {
    try {
      const attendanceRef = collection(db, 'Attendance');
      const courseRef = collection(db, 'Courses');
      
      // Get course details to get the course name
      const courseSnapshot = await getDocs(query(courseRef, where('id', '==', courseId)));
      const courseData = courseSnapshot.docs[0]?.data();
      
      if (!courseData) {
        console.log('Course not found for ID:', courseId);
        return {};
      }
      
      const courseName = courseData.name;
      const totalClasses = courseData.totalClasses || 0;
      
      console.log('Course found:', courseName, 'Total classes:', totalClasses);
      
      if (totalClasses === 0) {
        console.log('No total classes defined for course');
        return {};
      }
      
      // Get attendance records for this course and selected campus only.
      const attendanceQuery = selectedCampus
        ? query(attendanceRef, where('courseName', '==', courseName), where('campusCode', '==', selectedCampus))
        : query(attendanceRef, where('courseName', '==', courseName));
      const attendanceSnapshot = await getDocs(attendanceQuery);
      console.log('Attendance records found:', attendanceSnapshot.size);
      
      const attendanceCounts = {};
      
      // Initialize counts for all students
      studentIds.forEach(studentId => {
        attendanceCounts[studentId] = { present: 0, total: 0 };
      });
      
      // Count attendance for each student
      attendanceSnapshot.forEach(doc => {
        const data = doc.data();
        const b = dedupeAttendanceBuckets(data);

        console.log('Attendance record:', {
          courseName: data.courseName,
          presentStudents: b.presentCount,
          absentStudents: b.absentCount,
          leaveStudents: b.leaveCount
        });

        // For each attendance record, count it as conducted for ALL students
        // Not just those who are marked
        studentIds.forEach(studentId => {
          if (attendanceCounts[studentId]) {
            attendanceCounts[studentId].total += 1;
            if (b.presentSet.has(studentId) || b.leaveSet.has(studentId)) {
              attendanceCounts[studentId].present += 1;
            }
          }
        });
      });
      
      console.log('Attendance counts:', attendanceCounts);
      
      // Calculate percentages based on conducted classes
      const percentages = {};
      Object.keys(attendanceCounts).forEach(studentId => {
        const { present, total: conducted } = attendanceCounts[studentId];
        const percentage = conducted > 0 ? ((present / conducted) * 100).toFixed(1) : 0;
        percentages[studentId] = {
          percentage: parseFloat(percentage),
          present,
          conducted,
          totalPlanned: totalClasses
        };
        console.log(`Student ${studentId}: ${present}/${conducted} (of ${totalClasses}) = ${percentage}%`);
      });
      
      console.log('Final percentages:', percentages);
      return percentages;
    } catch (error) {
      console.error('Error calculating attendance percentage:', error);
      return {};
    }
  };

  // Dynamic columns based on selected course
  const getColumns = () => {
    const baseColumns = [
      {
        field: 'id',
        headerName: 'ID',
        width: 200,
        renderCell: params => (
          <Button
            variant="text"
            color="primary"
            onClick={() => handleStudentDetailOpen(params.row)}
            sx={{
              fontWeight: 600,
              textTransform: 'none',
              padding: 0,
              minWidth: 'auto'
            }}
          >
            {params.value}
          </Button>
        )
      },
      { field: 'name', headerName: 'Name', width: 180 },
      { 
        field: 'nameInUrdu', 
        headerName: 'Name (Urdu)', 
        width: 150,
        renderCell: (params) => {
          const value = params.value || '';
          const urdu = isUrduText(value);
          return (
            <Typography 
              className={urdu ? 'urdu' : ''} 
              variant="body2"
              sx={{
                ...getUrduSxProps(value),
                direction: 'ltr',
                textAlign: 'left'
              }}
            >
              {value}
            </Typography>
          );
        }
      },
      { field: 'fatherName', headerName: 'Father/Husband Name', width: 180 },
      { 
        field: 'fatherNameInUrdu', 
        headerName: 'Father/Husband Name (Urdu)', 
        width: 150,
        renderCell: (params) => {
          const value = params.value || '';
          const urdu = isUrduText(value);
          return (
            <Typography 
              className={urdu ? 'urdu' : ''} 
              variant="body2"
              sx={{
                ...getUrduSxProps(value),
                direction: 'ltr',
                textAlign: 'left'
              }}
            >
              {value}
            </Typography>
          );
        }
      },
      { 
        field: 'status', 
        headerName: 'Status', 
        width: 100,
        renderCell: params => {
          const status = params.row.status || 'Active';
          return (
            <Chip 
              label={getStudentStatusLabel(status)}
              size="small"
              sx={{
                backgroundColor: isActiveLikeStatus(status) ? '#4CAF50' : status === 'InActive' ? '#F44336' : '#FF9800',
                color: '#fff',
                fontWeight: 500,
                fontSize: '0.75rem'
              }}
            />
          );
        }
      }
    ];

    // Show overall marks summary, attendance, and per-test scores when a course is selected
    if (selectedCourse) {
      // Marks summary (overall % and totals)
      baseColumns.push({
        field: 'marksSummary',
        headerName: 'Marks Summary',
        width: 200,
        align: 'center',
        headerAlign: 'center',
        renderCell: params => {
          const percentage = params.row.percentage;
          const obtained = params.row.obtainedMarks || 0;
          const conducted = params.row.conductedTestsMaxMarks || 0;
          const conductedCount = params.row.conductedTests || 0;
          
          if (percentage === 'N/A') {
            return <Typography variant="body2" color="text.secondary">N/A</Typography>;
          }
          
          return (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
              <Chip
                label={`${percentage}%`}
                size="small"
                sx={{
                  bgcolor: parseFloat(percentage) >= 40 ? '#4CAF50' : '#F44336',
                  color: 'white',
                  fontWeight: 600,
                  minWidth: '60px'
                }}
              />
              <Typography variant="caption" color="text.secondary">
                {obtained}/{conducted} ({conductedCount} tests)
              </Typography>
            </Box>
          );
        }
      });

      // Add attendance summary column
      baseColumns.push({
        field: 'attendanceSummary',
        headerName: 'Attendance',
        width: 180,
        align: 'center',
        headerAlign: 'center',
        renderCell: params => {
          const percentage = params.row.attendancePercentage;
          const present = params.row.attendancePresent || 0;
          const conducted = params.row.attendanceConducted || 0;
          const totalPlanned = params.row.attendanceTotalPlanned || 0;
          
          if (percentage === null || percentage === undefined) {
            return <Typography variant="body2" color="text.secondary">N/A</Typography>;
          }
          
          return (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
              <Chip
                label={`${percentage}%`}
                size="small"
                sx={{
                  bgcolor: percentage > ATTENDANCE_PCT_GOOD_ABOVE ? '#4CAF50' : percentage >= ATTENDANCE_PCT_WARN_MIN ? '#FF9800' : '#F44336',
                  color: 'white',
                  fontWeight: 600,
                  minWidth: '60px'
                }}
              />
              <Typography variant="caption" color="text.secondary">
                P:{present} C:{conducted}/{totalPlanned}
              </Typography>
            </Box>
          );
        }
      });
      
      // Add per-test columns (grid view). Marks % summary is removed as requested.
      baseColumns.push(
        ...tests.map(test => {
          // Add "B.Q" prefix for test ID 108 (Bayan Ul Quran)
          let testName = test.testName;
          const testId = test.id || '';
          if (testId === '108' || testId.toString().includes('108') || testId.toString().includes('bayan')) {
            if (!testName.startsWith('B.Q')) {
              testName = `B.Q ${testName}`;
            }
          }
          return {
            field: test.id, 
            headerName: testName, 
            width: 100,
            align: 'center',
            headerAlign: 'center'
          };
        })
      );
    }

    baseColumns.push({
      field: 'Action', 
      headerName: 'Action', 
      width: isAdmin() ? 230 : 180, 
      sortable: false,
      filterable: false,
      align: 'center',
      headerAlign: 'center',
        renderCell: params => {
        const status = params.row.status || 'Active';
        const loading = Boolean(statusUpdating[params.row.id]);
        return (
          <Box sx={{ 
            display: 'flex', 
            gap: 1.5, 
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '100%'
          }}>
            {isActiveLikeStatus(status) ? (
              <Tooltip title="Mark InActive" arrow placement="top">
                <IconButton
                  size="medium"
                  onClick={() => handleStatusUpdate(params.row, 'InActive')}
                  disabled={loading}
                  sx={{
                    color: '#FF9800',
                    '&:hover': {
                      color: '#F57C00',
                      backgroundColor: 'rgba(255, 152, 0, 0.08)',
                      transform: 'scale(1.1)',
                    },
                    transition: 'all 0.2s ease-in-out'
                  }}
                >
                  <CancelIcon />
                </IconButton>
              </Tooltip>
            ) : (
              <Tooltip title="Mark Active" arrow placement="top">
                <IconButton
                  size="medium"
                  onClick={() => handleStatusUpdate(params.row, 'Active')}
                  disabled={loading}
                  sx={{
                    color: '#4CAF50',
                    '&:hover': {
                      color: '#2E7D32',
                      backgroundColor: 'rgba(76, 175, 80, 0.08)',
                      transform: 'scale(1.1)',
                    },
                    transition: 'all 0.2s ease-in-out'
                  }}
                >
                  <CheckCircleIcon />
                </IconButton>
              </Tooltip>
            )}
            {isAdmin() && (
              <Tooltip title="Delete student (admin)" arrow placement="top">
                <IconButton
                  size="medium"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteStudent(params.row);
                  }}
                  disabled={loading}
                  sx={{
                    color: '#F44336',
                    '&:hover': {
                      color: '#C62828',
                      backgroundColor: 'rgba(244, 67, 54, 0.08)',
                      transform: 'scale(1.1)',
                    },
                    transition: 'all 0.2s ease-in-out'
                  }}
                >
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        );
      }
    });

    return baseColumns;
  };



  return (
    <Box sx={{ p: 3, background: '#f8f9fa', minHeight: '100vh' }}>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Typography 
          variant="h4" 
          sx={{ 
            fontWeight: 700, 
            color: '#2E7D32',
            mb: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}
        >
          👥 Student Management
        </Typography>
        <Typography variant="body1" sx={{ color: '#666', mb: 2 }}>
          Manage student records, view academic performance, and update scores
        </Typography>
      </Box>

      {/* Filters Card */}
      <Paper 
        elevation={2} 
        sx={{ 
          p: 3, 
          mb: 3, 
          borderRadius: 3,
          background: 'linear-gradient(135deg, #fff 0%, #f8f9fa 100%)',
          border: '1px solid rgba(76, 175, 80, 0.1)'
        }}
      >
        <Typography 
          variant="h6" 
          sx={{ 
            fontWeight: 600, 
            color: '#2E7D32',
            mb: 3,
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}
        >
          🔍 Filters & Search
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'center' }}>
          {/* Campus first — subject list is scoped to the selected campus */}
          <FormControl sx={{ minWidth: 300 }}>
            <InputLabel sx={{ color: '#4CAF50' }}>Select Campus</InputLabel>
            <Select 
              value={selectedCampus} 
              onChange={handleCampusChange} 
              input={<OutlinedInput label="Select Campus" />} 
              disabled={loading || campuses.length === 1}
              endAdornment={loading && <CircularProgress size={20} />}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '&:hover fieldset': {
                    borderColor: '#4CAF50',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#2E7D32',
                  },
                },
              }}
            >
              {campuses.map(campus => (
                <MenuItem key={campus.id} value={campus.campusCode}>{campus.campusName} ({campus.campusCode})</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 300 }}>
            <InputLabel sx={{ color: '#4CAF50' }}>Filter by Subject</InputLabel>
            <Select 
              value={selectedCourse} 
              onChange={handleCourseChange} 
              input={<OutlinedInput label="Filter by Subject" />}
              disabled={loading || !selectedCampus}
              endAdornment={loading && <CircularProgress size={20} />}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '&:hover fieldset': {
                    borderColor: '#4CAF50',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#2E7D32',
                  },
                },
              }}
            >
              <MenuItem value="">All Courses</MenuItem>
              {coursesForCampus.map(course => (
                <MenuItem key={course.id} value={course.id}>
                  {course.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Gradebook button next to filters */}
          <Button
            variant="contained"
            color="primary"
            disabled={loading || !selectedCampus}
            onClick={() => {
              if (!selectedCampus) {
                showSnackbar('Please select a campus to open the Gradebook.', 'warning');
                return;
              }
              handleOpenGradebookDialog();
            }}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 2,
              px: 3
            }}
          >
            Open Gradebook
          </Button>

          {/* Context about which subject/tests are shown */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, color: 'text.secondary' }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#2E7D32' }}>
              Subject: {selectedCourseLabel}
            </Typography>
            <Typography variant="caption">
              Tests visible in Gradebook: {selectedTestsLabel}
            </Typography>
          </Box>
        </Box>

        {!selectedCampus && (
          <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
            Please select a campus to view students
          </Alert>
        )}
      </Paper>

      {/* Students Data Grid Card */}
      {selectedCampus && (
        <Paper 
          elevation={2} 
          sx={{ 
            p: 3, 
            mb: 3, 
            borderRadius: 3,
            background: 'linear-gradient(135deg, #fff 0%, #f8f9fa 100%)',
            border: '1px solid rgba(76, 175, 80, 0.1)'
          }}
        >
          <Typography 
            variant="h6" 
            sx={{ 
              fontWeight: 600, 
              color: '#2E7D32',
              mb: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}
          >
            📊 Student Records
          </Typography>
          
          <DataGrid
            rows={students}
            columns={getColumns()}
            pageSize={5000}
            loading={loading}
            slots={{ toolbar: StudentsGridToolbar }}
            getRowId={row => row.id}
            sx={{
              border: 'none',
              height: 600,
              '& .MuiDataGrid-root': {
                border: 'none',
              },
              '& .MuiDataGrid-cell': {
                borderBottom: '1px solid rgba(76, 175, 80, 0.1)',
                display: 'flex',
                alignItems: 'center',
              },
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: '#f8f9fa',
                borderBottom: '2px solid #4CAF50',
              },
            }}
          />
        </Paper>
      )}

      {/* Floating Action Buttons */}
      <Box sx={{ 
        position: 'fixed', 
        bottom: 20, 
        right: 20, 
        display: 'flex', 
        flexDirection: 'column', 
        gap: 2,
        zIndex: 1000
      }}>
        <Fab 
          color="secondary" 
          onClick={handleOpenImportDialog} 
          size="medium"
          disabled={loading}
          sx={{
            backgroundColor: loading ? '#ccc' : '#FF9800',
            color: '#fff',
            '&:hover': {
              backgroundColor: loading ? '#ccc' : '#F57C00',
              transform: loading ? 'none' : 'translateY(-2px)',
              boxShadow: loading ? 'none' : '0 6px 20px rgba(255, 152, 0, 0.3)',
            },
            transition: 'all 0.3s ease',
          }}
        >
          {loading ? <CircularProgress size={24} color="inherit" /> : <FileUploadIcon />}
        </Fab>
        <Fab 
          color="primary" 
          onClick={handleOpenDialog} 
          size="medium"
          disabled={loading}
          sx={{
            backgroundColor: loading ? '#ccc' : '#4CAF50',
            color: '#fff',
            '&:hover': {
              backgroundColor: loading ? '#ccc' : '#2E7D32',
              transform: loading ? 'none' : 'translateY(-2px)',
              boxShadow: loading ? 'none' : '0 6px 20px rgba(76, 175, 80, 0.3)',
            },
            transition: 'all 0.3s ease',
          }}
        >
          {loading ? <CircularProgress size={24} color="inherit" /> : <AddIcon />}
        </Fab>
      </Box>

      <Dialog 
        open={detailDialogOpen} 
        onClose={handleDetailDialogClose} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle sx={{ 
          bgcolor: '#4CAF50', 
          color: 'white', 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          py: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton 
              onClick={navigateToPreviousStudent}
              disabled={detailLoading || editMode || !detailStudent || students.findIndex(s => s.id === detailStudent.id) === 0}
              sx={{ 
                color: 'white',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
                '&.Mui-disabled': { color: 'rgba(255,255,255,0.3)' }
              }}
              size="small"
            >
              <NavigateBeforeIcon />
            </IconButton>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Student Details
              </Typography>
              {detailStudent && (
                <>
                  <Chip 
                    label={detailStudent.id} 
                    sx={{ 
                      bgcolor: 'rgba(255,255,255,0.2)', 
                      color: 'white',
                      fontWeight: 600
                    }} 
                  />
                  <Chip 
                    label={`${students.findIndex(s => s.id === detailStudent.id) + 1} / ${students.length}`} 
                    sx={{ 
                      bgcolor: 'rgba(255,255,255,0.3)', 
                      color: 'white',
                      fontWeight: 600,
                      fontSize: '0.85rem'
                    }} 
                    size="small"
                  />
                </>
              )}
            </Box>
            
            <IconButton 
              onClick={navigateToNextStudent}
              disabled={detailLoading || editMode || !detailStudent || students.findIndex(s => s.id === detailStudent.id) === students.length - 1}
              sx={{ 
                color: 'white',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
                '&.Mui-disabled': { color: 'rgba(255,255,255,0.3)' }
              }}
              size="small"
            >
              <NavigateNextIcon />
            </IconButton>
          </Box>

          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {!editMode && (
              <Button
                variant="contained"
                size="small"
                startIcon={<AssignmentIcon />}
                onClick={handlePrintStudentReport}
                disabled={detailLoading}
                sx={{
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  color: 'white',
                  textTransform: 'none',
                  fontWeight: 600,
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.3)',
                  }
                }}
              >
                Print
              </Button>
            )}
            {!editMode ? (
              <>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<EditIcon />}
                  onClick={handleEditClick}
                  disabled={detailLoading}
                  sx={{
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    textTransform: 'none',
                    fontWeight: 600,
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,0.3)',
                    }
                  }}
                >
                  Edit
                </Button>
                {isAdmin() && (
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<DeleteIcon />}
                    onClick={() => handleDeleteStudent(detailStudent)}
                    disabled={detailLoading}
                    sx={{
                      backgroundColor: 'rgba(211, 47, 47, 0.85)',
                      color: 'white',
                      textTransform: 'none',
                      fontWeight: 600,
                      '&:hover': {
                        backgroundColor: 'rgba(183, 28, 28, 0.95)',
                      }
                    }}
                  >
                    Delete
                  </Button>
                )}
              </>
            ) : (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                  onClick={handleSaveEdit}
                  disabled={saving}
                  sx={{
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    textTransform: 'none',
                    minWidth: '90px',
                    fontWeight: 600,
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,0.3)',
                    },
                    '&.Mui-disabled': {
                      backgroundColor: 'rgba(255,255,255,0.1)',
                      color: 'rgba(255,255,255,0.5)'
                    }
                  }}
                >
                  {saving ? 'Saving...' : 'Save'}
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<CloseIcon />}
                  onClick={handleCancelEdit}
                  disabled={saving}
                  sx={{
                    color: 'white',
                    borderColor: 'rgba(255,255,255,0.5)',
                    textTransform: 'none',
                    fontWeight: 600,
                    '&:hover': {
                      borderColor: 'white',
                      backgroundColor: 'rgba(255,255,255,0.1)',
                    }
                  }}
                >
                  Cancel
                </Button>
              </Box>
            )}
            <IconButton 
              onClick={handleDetailDialogClose}
              sx={{ color: 'white', ml: 1 }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ mt: 2, p: 3 }}>
          {detailLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
              <CircularProgress />
            </Box>
          ) : detailStudent ? (
            <Box>
              {/* Basic Profile Section */}
              <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 2, bgcolor: '#f8f9fa' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#2E7D32' }}>
                    📋 Profile Information
                  </Typography>
                  <Button
                    size="small"
                    onClick={() => setProfileExpanded(!profileExpanded)}
                    endIcon={profileExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    sx={{ textTransform: 'none', color: '#4CAF50', fontWeight: 600 }}
                  >
                    {profileExpanded ? 'Show Less' : 'Show All'}
                  </Button>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#4CAF50', textTransform: 'uppercase', letterSpacing: 0.6 }}>
                      Basic information
                    </Typography>
                    <Divider sx={{ mt: 0.5, mb: 0.5, borderColor: 'rgba(76,175,80,0.2)' }} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">Name</Typography>
                    {editMode ? (
                      <TextField
                        fullWidth
                        size="small"
                        value={editedStudent?.name || ''}
                        onChange={(e) => handleFieldChange('name', e.target.value)}
                      />
                    ) : (
                      <Typography variant="body1">{detailStudent.name || 'N/A'}</Typography>
                    )}
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">Student ID</Typography>
                    <Typography variant="body1" sx={{ color: editMode ? 'text.disabled' : 'text.primary' }}>
                      {detailStudent.id || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">Name (Urdu)</Typography>
                    {editMode ? (
                      <TextField
                        fullWidth
                        size="small"
                        value={editedStudent?.nameInUrdu || ''}
                        onChange={(e) => handleFieldChange('nameInUrdu', e.target.value)}
                        sx={{
                          '& .MuiInputBase-input': {
                            fontFamily: isUrduText(editedStudent?.nameInUrdu || '') 
                              ? "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', serif"
                              : 'inherit',
                            direction: 'ltr',
                            textAlign: 'left'
                          }
                        }}
                      />
                    ) : (
                      <Typography 
                        variant="body1" 
                        className={isUrduText(detailStudent.nameInUrdu || '') ? 'urdu' : ''}
                        sx={getUrduSxProps(detailStudent.nameInUrdu || '')}
                      >
                        {detailStudent.nameInUrdu || 'N/A'}
                      </Typography>
                    )}
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                    {editMode ? (
                      <FormControl fullWidth size="small">
                        <Select
                          value={editedStudent?.status || 'Active'}
                          onChange={(e) => handleFieldChange('status', e.target.value)}
                          displayEmpty
                        >
                          <MenuItem value={STUDENT_STATUS.ACTIVE}>Active</MenuItem>
                          <MenuItem value={STUDENT_STATUS.ACTIVE_LISTENER}>Active (listener)</MenuItem>
                          <MenuItem value={STUDENT_STATUS.INACTIVE}>Inactive</MenuItem>
                        </Select>
                      </FormControl>
                    ) : (
                      <Typography variant="body1">{getStudentStatusLabel(detailStudent.status)}</Typography>
                    )}
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">Gender</Typography>
                    <Typography variant="body1" sx={{ color: editMode ? 'text.disabled' : 'text.primary' }}>
                      {detailStudent.gender || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">CNIC</Typography>
                    {editMode ? (
                      <TextField
                        fullWidth
                        size="small"
                        value={editedStudent?.cnic || ''}
                        onChange={(e) => handleFieldChange('cnic', e.target.value)}
                      />
                    ) : (
                      <Typography variant="body1">{detailStudent.cnic || 'N/A'}</Typography>
                    )}
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">Date of Birth</Typography>
                    {editMode ? (
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                          label="Date of Birth"
                          format="DD/MM/YYYY"
                          value={editedStudent?.dateOfBirth ? dayjs(editedStudent.dateOfBirth) : null}
                          onChange={(value) => handleDobChange(value ? value.format('YYYY-MM-DD') : '')}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              size: 'small'
                            }
                          }}
                        />
                      </LocalizationProvider>
                    ) : (
                      <Typography variant="body1">
                        {detailStudent.dateOfBirth ? dayjs(detailStudent.dateOfBirth).format('DD/MM/YYYY') : 'N/A'}
                      </Typography>
                    )}
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">Age</Typography>
                    {editMode ? (
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        value={editedStudent?.age || ''}
                        onChange={(e) => handleFieldChange('age', e.target.value)}
                        helperText={editedStudent?.dateOfBirth ? 'Auto-calculated from Date of Birth' : ''}
                        inputProps={{ min: 0, max: 130 }}
                      />
                    ) : (
                      <Typography variant="body1">{detailStudent.age || 'N/A'}</Typography>
                    )}
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">Campus</Typography>
                    <Typography variant="body1" sx={{ color: editMode ? 'text.disabled' : 'text.primary' }}>
                      {campuses.find(c => c.campusCode === detailStudent.campusCode)?.campusName || detailStudent.campusCode || 'N/A'}
                    </Typography>
                  </Grid>
                </Grid>

                <Collapse in={profileExpanded}>
                  <Grid container spacing={2} sx={{ mt: 0.5 }}>
                    <Grid item xs={12}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: '#4CAF50', textTransform: 'uppercase', letterSpacing: 0.6, display: 'block', mt: 0.25 }}>
                        Family
                      </Typography>
                      <Divider sx={{ mt: 0.5, mb: 0.5, borderColor: 'rgba(76,175,80,0.2)' }} />
                    </Grid>
                    {(editMode ? editedStudent?.gender : detailStudent.gender) === 'Female' && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" color="text.secondary">W/O or D/O</Typography>
                        {editMode ? (
                          <FormControl fullWidth size="small">
                            <Select
                              value={editedStudent?.femaleNameRelation || ''}
                              onChange={(e) => handleFieldChange('femaleNameRelation', e.target.value)}
                            >
                              <MenuItem value="W/O">W/O (Wife of)</MenuItem>
                              <MenuItem value="D/O">D/O (Daughter of)</MenuItem>
                            </Select>
                          </FormControl>
                        ) : (
                          <Typography variant="body1">
                            {detailStudent.femaleNameRelation === 'W/O'
                              ? 'W/O (Wife of)'
                              : detailStudent.femaleNameRelation === 'D/O'
                                ? 'D/O (Daughter of)'
                                : '—'}
                          </Typography>
                        )}
                      </Grid>
                    )}
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        {getFatherNameFieldLabels(editMode ? editedStudent?.gender : detailStudent.gender).english}
                      </Typography>
                      {editMode ? (
                        <TextField
                          fullWidth
                          size="small"
                          value={editedStudent?.fatherName || ''}
                          onChange={(e) => handleFieldChange('fatherName', e.target.value)}
                        />
                      ) : (
                        <Typography variant="body1">{detailStudent.fatherName || 'N/A'}</Typography>
                      )}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        {getFatherNameFieldLabels(editMode ? editedStudent?.gender : detailStudent.gender).urdu}
                      </Typography>
                      {editMode ? (
                        <TextField
                          fullWidth
                          size="small"
                          value={editedStudent?.fatherNameInUrdu || ''}
                          onChange={(e) => handleFieldChange('fatherNameInUrdu', e.target.value)}
                          sx={{
                            '& .MuiInputBase-input': {
                              fontFamily: isUrduText(editedStudent?.fatherNameInUrdu || '') 
                                ? "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', serif"
                                : 'inherit',
                              direction: 'ltr',
                              textAlign: 'left'
                            }
                          }}
                        />
                      ) : (
                        <Typography 
                          variant="body1" 
                          className={isUrduText(detailStudent.fatherNameInUrdu || '') ? 'urdu' : ''}
                          sx={getUrduSxProps(detailStudent.fatherNameInUrdu || '')}
                        >
                          {detailStudent.fatherNameInUrdu || 'N/A'}
                        </Typography>
                      )}
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: '#4CAF50', textTransform: 'uppercase', letterSpacing: 0.6, display: 'block', mt: 0.5 }}>
                        Contact details
                      </Typography>
                      <Divider sx={{ mt: 0.5, mb: 0.5, borderColor: 'rgba(76,175,80,0.2)' }} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">Mobile</Typography>
                      {editMode ? (
                        <TextField
                          fullWidth
                          size="small"
                          value={editedStudent?.mobile || ''}
                          onChange={(e) => handleFieldChange('mobile', e.target.value)}
                        />
                      ) : (
                        <Typography variant="body1">{detailStudent.mobile || 'N/A'}</Typography>
                      )}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">Email</Typography>
                      {editMode ? (
                        <TextField
                          fullWidth
                          size="small"
                          type="email"
                          value={editedStudent?.email || ''}
                          onChange={(e) => handleFieldChange('email', e.target.value)}
                        />
                      ) : (
                        <Typography variant="body1">{detailStudent.email || 'N/A'}</Typography>
                      )}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">Phone (Residence)</Typography>
                      {editMode ? (
                        <TextField
                          fullWidth
                          size="small"
                          value={editedStudent?.phoneRes || ''}
                          onChange={(e) => handleFieldChange('phoneRes', e.target.value)}
                        />
                      ) : (
                        <Typography variant="body1">{detailStudent.phoneRes || 'N/A'}</Typography>
                      )}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">WhatsApp</Typography>
                      {editMode ? (
                        <TextField
                          fullWidth
                          size="small"
                          value={editedStudent?.whatsapp || ''}
                          onChange={(e) => handleFieldChange('whatsapp', e.target.value)}
                        />
                      ) : (
                        <Typography variant="body1">{detailStudent.whatsapp || 'N/A'}</Typography>
                      )}
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="text.secondary">Address</Typography>
                      {editMode ? (
                        <TextField
                          fullWidth
                          size="small"
                          multiline
                          rows={2}
                          value={editedStudent?.address || ''}
                          onChange={(e) => handleFieldChange('address', e.target.value)}
                        />
                      ) : (
                        <Typography variant="body1">{detailStudent.address || 'N/A'}</Typography>
                      )}
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: '#4CAF50', textTransform: 'uppercase', letterSpacing: 0.6, display: 'block', mt: 0.5 }}>
                        Academic &amp; program
                      </Typography>
                      <Divider sx={{ mt: 0.5, mb: 0.5, borderColor: 'rgba(76,175,80,0.2)' }} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">Course Taaruf</Typography>
                      {editMode ? (
                        <FormControl fullWidth size="small">
                          <Select
                            value={editedStudent?.courseTaaruf || ''}
                            onChange={(e) => {
                              handleFieldChange('courseTaaruf', e.target.value);
                              if (e.target.value !== 'Other') handleFieldChange('courseTaarufOther', '');
                            }}
                            displayEmpty
                          >
                            <MenuItem value="">None</MenuItem>
                            <MenuItem value="Daura">Daura</MenuItem>
                            <MenuItem value="Social Media">Social Media</MenuItem>
                            <MenuItem value="Rufqa-tanzeem">Rufqa-tanzeem</MenuItem>
                            <MenuItem value="Other">Other</MenuItem>
                          </Select>
                        </FormControl>
                      ) : (
                        <Typography variant="body1">
                          {detailStudent.courseTaaruf === 'Other' && detailStudent.courseTaarufOther
                            ? `Other: ${detailStudent.courseTaarufOther}`
                            : detailStudent.courseTaaruf || 'N/A'}
                        </Typography>
                      )}
                    </Grid>
                    {(editMode ? editedStudent?.courseTaaruf : detailStudent.courseTaaruf) === 'Other' && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" color="text.secondary">Course Taaruf (specify)</Typography>
                        {editMode ? (
                          <TextField
                            fullWidth
                            size="small"
                            value={editedStudent?.courseTaarufOther || ''}
                            onChange={(e) => handleFieldChange('courseTaarufOther', e.target.value)}
                          />
                        ) : (
                          <Typography variant="body1">{detailStudent.courseTaarufOther || 'N/A'}</Typography>
                        )}
                      </Grid>
                    )}
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">Qualification</Typography>
                      {editMode ? (
                        <FormControl fullWidth size="small">
                          <Select
                            value={editedStudent?.qualification || ''}
                            onChange={(e) => handleFieldChange('qualification', e.target.value)}
                            displayEmpty
                          >
                            <MenuItem value="">None</MenuItem>
                            <MenuItem value="Hifz">Hifz</MenuItem>
                            <MenuItem value="Nazra">Nazra</MenuItem>
                            <MenuItem value="Inter">Inter</MenuItem>
                            <MenuItem value="Matric">Matric</MenuItem>
                            <MenuItem value="Undergraduate">Undergraduate</MenuItem>
                            <MenuItem value="Graduate">Graduate</MenuItem>
                            <MenuItem value="Post Graduate">Post Graduate</MenuItem>
                          </Select>
                        </FormControl>
                      ) : (
                        <Typography variant="body1">{detailStudent.qualification || 'N/A'}</Typography>
                      )}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">Profession</Typography>
                      {editMode ? (
                        <TextField
                          fullWidth
                          size="small"
                          value={editedStudent?.profession || ''}
                          onChange={(e) => handleFieldChange('profession', e.target.value)}
                        />
                      ) : (
                        <Typography variant="body1">{detailStudent.profession || 'N/A'}</Typography>
                      )}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">Interview Status</Typography>
                      {editMode ? (
                        <FormControl fullWidth size="small">
                          <Select
                            value={editedStudent?.interview || ''}
                            onChange={(e) => handleFieldChange('interview', e.target.value)}
                            displayEmpty
                          >
                            <MenuItem value="">None</MenuItem>
                            <MenuItem value="Passed">Passed</MenuItem>
                            <MenuItem value="Failed">Failed</MenuItem>
                            <MenuItem value="Pending">Pending</MenuItem>
                          </Select>
                        </FormControl>
                      ) : (
                        <Typography variant="body1">{detailStudent.interview || 'N/A'}</Typography>
                      )}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">Scholarship</Typography>
                      {editMode ? (
                        <FormControl fullWidth size="small">
                          <Select
                            value={editedStudent?.scholarship || ''}
                            onChange={(e) => handleFieldChange('scholarship', e.target.value)}
                            displayEmpty
                          >
                            <MenuItem value="">None</MenuItem>
                            <MenuItem value="Yes">Yes</MenuItem>
                            <MenuItem value="No">No</MenuItem>
                          </Select>
                        </FormControl>
                      ) : (
                        <Typography variant="body1">{detailStudent.scholarship || 'N/A'}</Typography>
                      )}
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="text.secondary">Reason</Typography>
                      {editMode ? (
                        <TextField
                          fullWidth
                          size="small"
                          multiline
                          rows={2}
                          value={editedStudent?.reason || ''}
                          onChange={(e) => handleFieldChange('reason', e.target.value)}
                        />
                      ) : (
                        <Typography variant="body1">{detailStudent.reason || 'N/A'}</Typography>
                      )}
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: '#4CAF50', textTransform: 'uppercase', letterSpacing: 0.6, display: 'block', mt: 0.5 }}>
                        Membership
                      </Typography>
                      <Divider sx={{ mt: 0.5, mb: 0.5, borderColor: 'rgba(76,175,80,0.2)' }} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">Anjuman Member At Joining</Typography>
                      {editMode ? (
                        <FormControl fullWidth size="small">
                          <Select
                            value={editedStudent?.isAnjumanMemberAtJoining || ''}
                            onChange={(e) => handleFieldChange('isAnjumanMemberAtJoining', e.target.value)}
                            displayEmpty
                          >
                            <MenuItem value="">Select</MenuItem>
                            <MenuItem value="Yes">Yes</MenuItem>
                            <MenuItem value="No">No</MenuItem>
                          </Select>
                        </FormControl>
                      ) : (
                        <Typography variant="body1">{detailStudent.isAnjumanMemberAtJoining || 'N/A'}</Typography>
                      )}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">Anjuman Member After Joining</Typography>
                      {editMode ? (
                        <FormControl fullWidth size="small">
                          <Select
                            value={editedStudent?.isAnjumanMemberAfterJoining || ''}
                            onChange={(e) => handleFieldChange('isAnjumanMemberAfterJoining', e.target.value)}
                            displayEmpty
                          >
                            <MenuItem value="">Select</MenuItem>
                            <MenuItem value="Yes">Yes</MenuItem>
                            <MenuItem value="No">No</MenuItem>
                          </Select>
                        </FormControl>
                      ) : (
                        <Typography variant="body1">{detailStudent.isAnjumanMemberAfterJoining || 'N/A'}</Typography>
                      )}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">Tanzeem Member At Joining</Typography>
                      {editMode ? (
                        <FormControl fullWidth size="small">
                          <Select
                            value={editedStudent?.isTanzeemMemberAtJoining || ''}
                            onChange={(e) => handleFieldChange('isTanzeemMemberAtJoining', e.target.value)}
                            displayEmpty
                          >
                            <MenuItem value="">Select</MenuItem>
                            <MenuItem value="Yes">Yes</MenuItem>
                            <MenuItem value="No">No</MenuItem>
                          </Select>
                        </FormControl>
                      ) : (
                        <Typography variant="body1">{detailStudent.isTanzeemMemberAtJoining || 'N/A'}</Typography>
                      )}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">Tanzeem Member After Joining</Typography>
                      {editMode ? (
                        <FormControl fullWidth size="small">
                          <Select
                            value={editedStudent?.isTanzeemMemberAfterJoining || ''}
                            onChange={(e) => handleFieldChange('isTanzeemMemberAfterJoining', e.target.value)}
                            displayEmpty
                          >
                            <MenuItem value="">Select</MenuItem>
                            <MenuItem value="Yes">Yes</MenuItem>
                            <MenuItem value="No">No</MenuItem>
                          </Select>
                        </FormControl>
                      ) : (
                        <Typography variant="body1">{detailStudent.isTanzeemMemberAfterJoining || 'N/A'}</Typography>
                      )}
                    </Grid>
                  </Grid>
                </Collapse>
              </Paper>

              {/* Attendance Summary Section */}
              <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#2E7D32' }}>
                    📊 Subject-Wise Attendance
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleDownloadAttendanceSummary}
                      startIcon={<DownloadIcon />}
                      sx={{
                        borderColor: '#4CAF50',
                        color: '#4CAF50',
                        '&:hover': { borderColor: '#45a049', bgcolor: '#e8f5e9' },
                        textTransform: 'none',
                        fontWeight: 500
                      }}
                    >
                      Download
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      onClick={handleViewAllAttendance}
                      startIcon={<AssignmentIcon />}
                      sx={{
                        bgcolor: '#4CAF50',
                        '&:hover': { bgcolor: '#45a049' },
                        textTransform: 'none',
                        fontWeight: 500
                      }}
                    >
                      View All Attendance
                    </Button>
                  </Box>
                </Box>
                {detailAttendanceSummary.length > 0 ? (
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                          <TableCell sx={{ fontWeight: 600 }}>Course</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 600 }}>Present</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 600 }}>Absent</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 600 }}>Leaves</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 600 }}>Conducted</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 600 }}>Total Planned</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 600 }}>%</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {detailAttendanceSummary.map(row => (
                          <TableRow key={row.courseName}>
                            <TableCell>{row.courseName}</TableCell>
                            <TableCell align="center">{row.present}</TableCell>
                            <TableCell align="center">{row.absent}</TableCell>
                            <TableCell align="center">{row.leaves}</TableCell>
                            <TableCell align="center">{row.conducted}</TableCell>
                            <TableCell align="center">{row.totalPlanned}</TableCell>
                            <TableCell align="center">
                              <Chip 
                                label={`${row.percentage}%`}
                                size="small"
                                sx={{
                                  bgcolor: parseFloat(row.percentage) > ATTENDANCE_PCT_GOOD_ABOVE ? '#4CAF50' : 
                                           parseFloat(row.percentage) >= ATTENDANCE_PCT_WARN_MIN ? '#FF9800' : '#F44336',
                                  color: 'white',
                                  fontWeight: 600
                                }}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography variant="body2" color="text.secondary">No attendance records found.</Typography>
                )}
              </Paper>


              {/* Marks Section */}
              <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#2E7D32' }}>
                    📝 Subject-Wise Marks Summary
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={(e) => setMarksDownloadMenuAnchor(e.currentTarget)}
                      startIcon={<DownloadIcon />}
                      sx={{
                        borderColor: '#4CAF50',
                        color: '#4CAF50',
                        '&:hover': { borderColor: '#45a049', bgcolor: '#e8f5e9' },
                        textTransform: 'none',
                        fontWeight: 500
                      }}
                    >
                      Download
                    </Button>
                    <Menu
                      anchorEl={marksDownloadMenuAnchor}
                      open={Boolean(marksDownloadMenuAnchor)}
                      onClose={() => setMarksDownloadMenuAnchor(null)}
                    >
                      <MenuItem onClick={() => {
                        handleDownloadMarksSummary();
                        setMarksDownloadMenuAnchor(null);
                      }}>
                        Download Summary
                      </MenuItem>
                      <MenuItem onClick={() => {
                        handleDownloadMarksBreakdown();
                        setMarksDownloadMenuAnchor(null);
                      }}>
                        Download Breakdown
                      </MenuItem>
                    </Menu>
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      onClick={handleOpenGradebookDialog}
                      startIcon={<MenuBookIcon />}
                      sx={{
                        bgcolor: '#4CAF50',
                        '&:hover': { bgcolor: '#45a049' },
                        textTransform: 'none',
                        fontWeight: 500
                      }}
                    >
                      Open Gradebook
                    </Button>
                  </Box>
                </Box>
                {detailMarks.length > 0 ? (
                  <Box>
                    {(() => {
                      // Group marks by course/subject and calculate totals
                      const marksByCourse = detailMarks.reduce((acc, mark) => {
                        const courseName = mark.courseName;
                        if (!acc[courseName]) {
                          acc[courseName] = {
                            marks: [],
                            totalScore: 0,
                            totalOutOf: 0
                          };
                        }
                        acc[courseName].marks.push(mark);
                        acc[courseName].totalScore += Number(mark.score || 0);
                        const maxScore = mark.maxScore === 'N/A' ? 0 : Number(mark.maxScore || 0);
                        acc[courseName].totalOutOf += maxScore;
                        return acc;
                      }, {});

                      // Create summary array sorted A to Z
                      const summary = Object.entries(marksByCourse)
                        .map(([courseName, data]) => {
                          const percentage = data.totalOutOf > 0 
                            ? ((data.totalScore / data.totalOutOf) * 100).toFixed(2)
                            : '0.00';
                          return {
                            courseName,
                            totalScore: data.totalScore,
                            totalOutOf: data.totalOutOf,
                            percentage,
                            testsConducted: data.marks.length,
                            marks: data.marks
                          };
                        })
                        .sort((a, b) => a.courseName.localeCompare(b.courseName));

                      return (
                        <>
                          {/* Summary Table */}
                          <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                            <Table size="small">
                              <TableHead>
                                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                                  <TableCell sx={{ fontWeight: 600 }}>Subject</TableCell>
                                  <TableCell align="center" sx={{ fontWeight: 600 }}>Total Score</TableCell>
                                  <TableCell align="center" sx={{ fontWeight: 600 }}>Out Of</TableCell>
                                  <TableCell align="center" sx={{ fontWeight: 600 }}>Tests Conducted</TableCell>
                                  <TableCell align="center" sx={{ fontWeight: 600 }}>%</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {summary.map(row => (
                                  <TableRow key={row.courseName}>
                                    <TableCell sx={{ fontWeight: 500 }}>{row.courseName}</TableCell>
                                    <TableCell align="center">{row.totalScore}</TableCell>
                                    <TableCell align="center">{row.totalOutOf}</TableCell>
                                    <TableCell align="center">{row.testsConducted}</TableCell>
                                    <TableCell align="center">
                                      <Chip 
                                        label={`${row.percentage}%`}
                                        size="small"
                                        sx={{
                                          bgcolor: parseFloat(row.percentage) >= 40 ? '#4CAF50' : '#F44336',
                                          color: 'white',
                                          fontWeight: 600
                                        }}
                                      />
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>

                          {/* Detailed Breakdown */}
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 3, mb: 2 }}>
                            <Typography variant="h6" sx={{ fontWeight: 600, color: '#2E7D32' }}>
                              📋 Detailed Marks Breakdown
                            </Typography>
                            <IconButton
                              onClick={() => setDetailedMarksExpanded(!detailedMarksExpanded)}
                              size="small"
                              sx={{
                                color: '#2E7D32',
                                '&:hover': { bgcolor: '#e8f5e9' }
                              }}
                            >
                              {detailedMarksExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                          </Box>
                          <Collapse in={detailedMarksExpanded}>
                            {summary.map(({ courseName, marks }) => (
                            <Box key={courseName} sx={{ mb: 3 }}>
                              <Typography 
                                variant="subtitle1" 
                                sx={{ 
                                  mb: 1.5, 
                                  fontWeight: 600, 
                                  color: '#1976d2',
                                  pb: 1,
                                  borderBottom: '2px solid #e0e0e0'
                                }}
                              >
                                {courseName}
                              </Typography>
                              <TableContainer component={Paper} variant="outlined">
                                <Table size="small">
                                  <TableHead>
                                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                                      <TableCell sx={{ fontWeight: 600 }}>Assessment</TableCell>
                                      <TableCell align="center" sx={{ fontWeight: 600 }}>Score</TableCell>
                                      <TableCell align="center" sx={{ fontWeight: 600 }}>Out Of</TableCell>
                                      <TableCell align="center" sx={{ fontWeight: 600 }}>%</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {marks.map(mark => (
                                      <TableRow key={mark.id}>
                                        <TableCell>{mark.testName}</TableCell>
                                        <TableCell align="center">{mark.score}</TableCell>
                                        <TableCell align="center">{mark.maxScore ?? 'N/A'}</TableCell>
                                        <TableCell align="center">
                                          {mark.percentage ? (
                                            <Chip 
                                              label={`${mark.percentage}%`}
                                              size="small"
                                              sx={{
                                                bgcolor: parseFloat(mark.percentage) >= 40 ? '#4CAF50' : '#F44336',
                                                color: 'white',
                                                fontWeight: 600
                                              }}
                                            />
                                          ) : 'N/A'}
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            </Box>
                          ))}
                          </Collapse>
                        </>
                      );
                    })()}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">No marks recorded.</Typography>
                )}
              </Paper>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">Unable to load student details.</Typography>
          )}
        </DialogContent>
      </Dialog>

      <AddStudentDialog open={openAddStudentDialog} onClose={handleCloseDialog} />
      <ExcelImportDialog open={openImportDialog} onClose={handleCloseImportDialog} />
      <GradebookDialog
        open={openGradebookDialog}
        onClose={handleCloseGradebookDialog}
        tests={selectedCourse ? tests : allTests}
        students={students.filter(s => isActiveLikeStatus(s.status))}
        marksByStudent={marksByStudent}
        courseLabel={selectedCourseLabel}
        courses={coursesForCampus}
      />

      {/* Reason Dialog for InActive Status */}
      <Dialog 
        open={inactiveReasonDialog} 
        onClose={handleCancelInactive}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600, color: '#F44336' }}>
          Mark Student as InActive
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
              Please provide a reason for marking "{studentToMarkInactive?.name}" as InActive:
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              value={inactiveReason}
              onChange={(e) => setInactiveReason(e.target.value)}
              placeholder="Enter reason (e.g., Left the program, Transferred, etc.)"
              variant="outlined"
              autoFocus
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: '#F44336',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#F44336',
                  },
                },
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={handleCancelInactive}
            variant="outlined"
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmInactive}
            variant="contained"
            color="error"
            disabled={!inactiveReason.trim()}
            sx={{ 
              textTransform: 'none',
              minWidth: '100px'
            }}
          >
            Mark InActive
          </Button>
        </DialogActions>
      </Dialog>

      {/* View All Attendance Dialog */}
      <Dialog
        open={allAttendanceDialog}
        onClose={() => {
          setAllAttendanceDialog(false);
          setSelectedAttendanceRows([]);
        }}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle sx={{ 
          bgcolor: '#4CAF50', 
          color: 'white', 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          py: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Complete Attendance History
            </Typography>
            {detailStudent && (
              <>
                <Chip 
                  label={detailStudent.id} 
                  sx={{ 
                    bgcolor: 'rgba(255,255,255,0.2)', 
                    color: 'white',
                    fontWeight: 600
                  }} 
                />
                <Chip 
                  label={detailStudent.name} 
                  sx={{ 
                    bgcolor: 'rgba(255,255,255,0.2)', 
                    color: 'white',
                    fontWeight: 600
                  }} 
                />
              </>
            )}
          </Box>
          <IconButton 
            onClick={() => {
              setAllAttendanceDialog(false);
              setSelectedAttendanceRows([]);
            }}
            sx={{ color: 'white' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ mt: 2, p: 3 }}>
          {loadingAllAttendance ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
              <CircularProgress size={60} />
            </Box>
          ) : (
            <>
              <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  {selectedAttendanceRows.length > 0 
                    ? `${selectedAttendanceRows.length} record(s) selected` 
                    : 'Select records to print or print all'}
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handlePrintAllAttendance}
                  startIcon={<AssignmentIcon />}
                  sx={{
                    bgcolor: '#4CAF50',
                    '&:hover': { bgcolor: '#45a049' }
                  }}
                >
                  Print Report
                </Button>
              </Box>
              <DataGrid
                rows={allAttendanceData}
                columns={[
                  {
                    field: 'courseName',
                    headerName: 'Course',
                    width: 200,
                    flex: 1
                  },
                  {
                    field: 'startTime',
                    headerName: 'Date & Time',
                    width: 180
                  },
                  {
                    field: 'topic',
                    headerName: 'Topic',
                    width: 200,
                    flex: 1,
                    renderCell: (params) => {
                      const value = params.value || '';
                      const urdu = isUrduText(value);
                      return (
                        <Typography 
                          className={urdu ? 'urdu' : ''} 
                          variant="body2"
                          sx={{
                            ...getUrduSxProps(value),
                            direction: 'ltr',
                            textAlign: 'left'
                          }}
                        >
                          {value || '-'}
                        </Typography>
                      );
                    }
                  },
                  {
                    field: 'status',
                    headerName: 'Status',
                    width: 120,
                    renderCell: (params) => (
                      <Chip
                        label={params.value}
                        size="small"
                        color={
                          params.value === 'Present' ? 'success' :
                          params.value === 'Absent' ? 'error' : 'warning'
                        }
                      />
                    )
                  },
                  {
                    field: 'reason',
                    headerName: 'Leave Reason',
                    width: 200,
                    flex: 1,
                    renderCell: (params) => params.value || '-'
                  },
                  {
                    field: 'comments',
                    headerName: 'Comments',
                    width: 200,
                    flex: 1,
                    renderCell: (params) => {
                      const value = params.value || '';
                      const urdu = isUrduText(value);
                      return (
                        <Typography 
                          className={urdu ? 'urdu' : ''} 
                          variant="body2"
                          sx={{
                            ...getUrduSxProps(value),
                            direction: 'ltr',
                            textAlign: 'left'
                          }}
                        >
                          {value || '-'}
                        </Typography>
                      );
                    }
                  },
                  {
                    field: 'actions',
                    headerName: 'Action',
                    width: 120,
                    sortable: false,
                    renderCell: (params) => (
                      <Button
                        variant="outlined"
                        color="primary"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (params.row.date) {
                            navigate(`/attendance?date=${params.row.date}`);
                          }
                        }}
                        sx={{
                          borderRadius: 2,
                          textTransform: 'none',
                          fontWeight: 500
                        }}
                      >
                        Modify
                      </Button>
                    )
                  }
                ]}
                initialState={{
                  pagination: {
                    paginationModel: { pageSize: 10, page: 0 },
                  },
                }}
                pageSizeOptions={[5, 10, 25, 50, 100]}
                checkboxSelection
                disableRowSelectionOnClick
                onRowSelectionModelChange={(newSelection) => {
                  setSelectedAttendanceRows(newSelection);
                }}
                rowSelectionModel={selectedAttendanceRows}
                slots={{ toolbar: GridToolbar }}
                sx={{
                  height: 600,
                  border: 'none',
                  '& .MuiDataGrid-root': {
                    border: 'none',
                  },
                  '& .MuiDataGrid-cell': {
                    borderBottom: '1px solid rgba(76, 175, 80, 0.1)',
                  },
                  '& .MuiDataGrid-columnHeaders': {
                    backgroundColor: '#f8f9fa',
                    borderBottom: '2px solid #4CAF50',
                    fontWeight: 600
                  },
                  '& .MuiDataGrid-footerContainer': {
                    borderTop: '2px solid #4CAF50',
                  }
                }}
              />
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{ zIndex: 99999 }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default StudentList;
